import { BACKEND_URL, getAuthHeaders } from "../lib/api";
import { useState, useEffect, useRef, useCallback, useMemo, Suspense, lazy } from "react";
import { useNavigate } from "react-router-dom";
// Lazy load the heavy Globe component
const Globe = lazy(() => import("react-globe.gl"));
import { motion, AnimatePresence, useScroll, useTransform, useSpring, useMotionValueEvent, useAnimation } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { MapPin, Search, LogIn, UserPlus, Globe as GlobeIcon, Music, Users, Sparkles, Megaphone, Volume2, VolumeX, Plane, Briefcase, Shield, Download, X, Loader2, CalendarDays, Radio, Building2, Award, Heart, QrCode, CalendarClock, Camera, Compass, Rocket } from "lucide-react";
import { useLanguage } from "../lib/LanguageContext";
import { LanguageSelector } from "../lib/LanguageSelector";
import { PageSEO } from "../components/SEO";
import usePageTracking from "../hooks/usePageTracking";

// sQawk Logo - Official bird logo with text
const SQAWK_LOGO = "https://customer-assets.emergentagent.com/job_sqawk-social-4/artifacts/fvlilxsv_ChatGPT%20Image%20Jan%2027%2C%202026%2C%2007_59_06%20PM.png";

// Background music tracks that auto-play
const LANDING_MUSIC = [
  "https://cdn.pixabay.com/audio/2024/11/04/audio_b59e854382.mp3", // Chill ambient
  "https://cdn.pixabay.com/audio/2022/10/25/audio_552e01d070.mp3", // Uplifting
  "https://cdn.pixabay.com/audio/2024/09/11/audio_bf97327822.mp3", // Positive vibes
];

// Sample cities data for globe
const CITIES = [
  { name: "New York", country: "USA", lat: 40.7128, lng: -74.006, pop: 8336817 },
  { name: "Los Angeles", country: "USA", lat: 34.0522, lng: -118.2437, pop: 3979576 },
  { name: "London", country: "UK", lat: 51.5074, lng: -0.1278, pop: 8982000 },
  { name: "Tokyo", country: "Japan", lat: 35.6762, lng: 139.6503, pop: 13960000 },
  { name: "Paris", country: "France", lat: 48.8566, lng: 2.3522, pop: 2161000 },
  { name: "Sydney", country: "Australia", lat: -33.8688, lng: 151.2093, pop: 5312000 },
  { name: "Dubai", country: "UAE", lat: 25.2048, lng: 55.2708, pop: 3331000 },
  { name: "Singapore", country: "Singapore", lat: 1.3521, lng: 103.8198, pop: 5686000 },
  { name: "Berlin", country: "Germany", lat: 52.52, lng: 13.405, pop: 3645000 },
  { name: "Toronto", country: "Canada", lat: 43.6532, lng: -79.3832, pop: 2930000 },
  { name: "Miami", country: "USA", lat: 25.7617, lng: -80.1918, pop: 470914 },
  { name: "Barcelona", country: "Spain", lat: 41.3874, lng: 2.1686, pop: 1620000 },
  { name: "Amsterdam", country: "Netherlands", lat: 52.3676, lng: 4.9041, pop: 872680 },
  { name: "Seoul", country: "South Korea", lat: 37.5665, lng: 126.978, pop: 9776000 },
  { name: "Mumbai", country: "India", lat: 19.076, lng: 72.8777, pop: 12442000 },
  { name: "São Paulo", country: "Brazil", lat: -23.5505, lng: -46.6333, pop: 12325000 },
  { name: "Mexico City", country: "Mexico", lat: 19.4326, lng: -99.1332, pop: 8918653 },
  { name: "Cairo", country: "Egypt", lat: 30.0444, lng: 31.2357, pop: 9540000 },
  { name: "Moscow", country: "Russia", lat: 55.7558, lng: 37.6173, pop: 11920000 },
  { name: "Hong Kong", country: "China", lat: 22.3193, lng: 114.1694, pop: 7500700 },
];

// Generate arcs between cities with green/cyan colors - fewer for better performance
const generateArcs = () => {
  const arcs = [];
  for (let i = 0; i < 6; i++) {
    const from = CITIES[Math.floor(Math.random() * CITIES.length)];
    const to = CITIES[Math.floor(Math.random() * CITIES.length)];
    if (from !== to) {
      arcs.push({
        startLat: from.lat,
        startLng: from.lng,
        endLat: to.lat,
        endLng: to.lng,
        color: ["rgba(16, 185, 129, 0.5)", "rgba(34, 211, 238, 0.5)", "rgba(52, 211, 153, 0.4)"][Math.floor(Math.random() * 3)],
      });
    }
  }
  return arcs;
};

export default function LandingPage({ setUser }) {
  const navigate = useNavigate();
  const globeRef = useRef();
  const audioRef = useRef(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showCitySearch, setShowCitySearch] = useState(false);
  const [citySearch, setCitySearch] = useState("");
  const [selectedCity, setSelectedCity] = useState(null);
  const [arcsData, setArcsData] = useState([]);
  const [globeReady, setGlobeReady] = useState(false);
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const [musicMuted, setMusicMuted] = useState(false);
  
  // Track page view
  usePageTracking("landing");
  
  // DESKTOP SCROLLING FIX: Force native browser scrolling
  useEffect(() => {
    // Remove any scroll-blocking styles
    document.documentElement.style.overflow = 'auto';
    document.documentElement.style.height = 'auto';
    document.body.style.overflow = 'visible';
    document.body.style.height = 'auto';
    document.body.style.position = 'static';
    
    // Ensure smooth scrolling
    document.documentElement.style.scrollBehavior = 'smooth';
    
    // Remove any event listeners that might block scrolling
    const preventScrollBlock = (e) => {
      // Allow all scroll events to propagate
      e.stopPropagation = () => {};
    };
    
    return () => {
      // Cleanup
      document.documentElement.style.overflow = '';
      document.documentElement.style.height = '';
      document.body.style.overflow = '';
      document.body.style.height = '';
      document.body.style.position = '';
      document.documentElement.style.scrollBehavior = '';
    };
  }, []);

  // PWA Install prompt capture - only for mobile
  useEffect(() => {
    // Check if desktop - don't show PWA install on desktop
    const userAgent = navigator.userAgent.toLowerCase();
    const isDesktop = /windows|macintosh|linux/.test(userAgent) && !/android|iphone|ipad|ipod/.test(userAgent);
    
    if (isDesktop) return; // Skip PWA prompt on desktop
    
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      // Show install banner after 3 seconds if not dismissed
      const dismissed = localStorage.getItem('sqawk_install_dismissed');
      if (!dismissed) {
        setTimeout(() => setShowInstallBanner(true), 3000);
      }
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!installPrompt) return;
    
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    
    if (outcome === 'accepted') {
      toast.success('🎉 sQawk installed! Find it on your home screen.');
    }
    
    setInstallPrompt(null);
    setShowInstallBanner(false);
  };

  const dismissInstallBanner = () => {
    setShowInstallBanner(false);
    localStorage.setItem('sqawk_install_dismissed', 'true');
  };

  // Login handler for overlay
  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!subscribeEmail || !subscribeEmail.includes('@')) return;
    setSubscribing(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/subscribe-tester`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: subscribeEmail })
      });
      if (res.ok) { setSubscribed(true); setSubscribeEmail(''); }
    } catch {}
    setSubscribing(false);
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) {
      setLoginError('Enter email and password');
      return;
    }
    setLoginLoading(true);
    setLoginError('');
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: loginEmail.trim().toLowerCase(), 
          password: loginPassword,
          remember_me: rememberMe
        }),
      });
      if (!response.ok) {
        if (response.status === 401) {
          setLoginError('Invalid email or password');
          setLoginLoading(false);
          return;
        }
        let msg = 'Login failed. Please try again.';
        try { const d = await response.json(); msg = d.detail || msg; } catch {}
        setLoginError(msg);
        setLoginLoading(false);
        return;
      }
      const data = await response.json();
      if (data.session_token) {
        localStorage.setItem('sqawk_session', data.session_token);
        localStorage.setItem('sqawk_session_start', Date.now().toString());
        localStorage.setItem('sqawk_remember_me', rememberMe ? 'true' : 'false');
        if (data.user) setUser(data.user);
        setLoginSuccess(true);
        setTimeout(() => navigate('/dashboard', { replace: true }), 400);
      } else {
        setLoginError('Login failed. Please try again.');
      }
    } catch {
      setLoginError('Unable to connect. Please try again.');
    }
    setLoginLoading(false);
  };
  
  // Dynamic cities from API - user-joined cities
  const [dynamicCities, setDynamicCities] = useState([]);
  
  // Business directory countdown counter
  const [businessCount, setBusinessCount] = useState(0);
  const [recentBusinesses, setRecentBusinesses] = useState([]); // last few joined for rotating ticker
  const [tickerIndex, setTickerIndex] = useState(0);
  const [displayCount, setDisplayCount] = useState(100000);
  
  // PWA Install prompt
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  
  // Registration success animation state
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [successAnimationPhase, setSuccessAnimationPhase] = useState(0);
  const [pendingUser, setPendingUser] = useState(null);

  // Login overlay state
  const [showLoginOverlay, setShowLoginOverlay] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => {
    const stored = localStorage.getItem('sqawk_remember_me');
    return stored === null ? true : stored === 'true';
  });

  // Use shared language context
  const { language, setLanguage: setGlobalLanguage } = useLanguage();
  
  // Sync with global language
  const handleLanguageChange = (newLang) => {
    setGlobalLanguage(newLang);
  };
  
  // Translations
  const translations = {
    en: {
      tagline: "sQawk Across the World.",
      subtitle: "A location-first social platform where geography meets melody. Collect cities, curate your squad, and broadcast your anthem to the world.",
      getStarted: "Get Started",
      secure: "Secure",
      joinsQawk: "Join sQawk!",
      welcome: "Welcome to sQawk",
      joinCommunity: "Join the global community",
      login: "Login",
      signUp: "Sign Up",
      signIn: "Sign In",
      signingIn: "Signing in...",
      createAccount: "Create Account",
      creatingAccount: "Creating account...",
      name: "Name",
      username: "Username",
      email: "Email",
      password: "Password",
      city: "City",
      state: "State",
      country: "Country",
      optional: "opt",
      dataSecure: "Your data is encrypted and secure",
      signUpGoogle: "Sign up with Google",
      signInGoogle: "Sign in with Google",
      orRegister: "OR REGISTER WITH EMAIL",
      orLogin: "OR LOGIN WITH EMAIL",
      findTravel: "Find Travel Friends",
      connectTravelers: "Connect with fellow travelers",
      collectCities: "Collect Cities",
      buildFootprint: "Build your global footprint",
      goLive: "Go Live",
      broadcastWorld: "Broadcast to the world",
      promoteYourself: "Promote Yourself",
      getDiscovered: "Get discovered worldwide",
      shareBusiness: "Share Your Business",
      showcaseBrand: "Showcase your brand & services",
      createProfile: "Create your sQawk Profile Today",
      joinGlobal: "Join the global community and start connecting",
      missionP1: "sQawk is a next-generation social platform inspired by what worked in the early days of social media — before engagement was engineered and attention was exploited.",
      missionP2Intro: "We believe:",
      missionP2: "Conversation beats clout. Community beats virality. Authenticity beats algorithms.",
      missionP3Start: "sQawk is",
      missionP3End: "Social Media for People.",
      alreadyHaveAccount: "Already have an account?",
      // New translations
      shareAnthem: "Share Your Anthem",
      showcaseMusic: "Showcase your music taste",
      top5Squad: "Top 5 Squad",
      showcaseClosestFriends: "Showcase your closest friends",
      claimYourCity: "Claim Your City",
      searchClaimCity: "Search & Claim Your City!",
      globalBusinessDirectory: "100,000 Global Business Directory",
      listBusinessWorld: "List your business to the world for just $1!",
      liveCounter: "Live Counter",
      liveUsers: "Live Users",
      streamingNow: "Streaming Now",
      // Additional landing page translations
      beFoundingMember: "Be the founding member of your hometown's sQawk community!",
      startConversations: "Start conversations, share local news, and connect with people in your city.",
      freeToClaim: "Free to claim • Become a founding member • Build your local community",
      bePartOfGlobal: "Be Part of a GLOBAL Experiment! Our goal is to host",
      businessesWorldwide: "businesses worldwide.",
      payJust1Dollar: "Pay just $1 and get your business featured in our live ticker and searchable directory!",
      clickPayAddBusiness: "Click Here to Pay $1 & Add Your Business!",
      noAccountRequired: "No account required • Instant listing • Global reach",
      spotsRemaining: "Spots Remaining",
      outOf100k: "out of 100,000",
      joinedCount: "joined",
      goalText: "Goal: 100K"
    },
    es: {
      tagline: "sQawk Por Todo el Mundo.",
      subtitle: "Una plataforma social donde la geografía se encuentra con la melodía. Colecciona ciudades, cura tu grupo y transmite tu himno al mundo.",
      getStarted: "Comenzar",
      secure: "Seguro",
      joinsQawk: "¡Únete a sQawk!",
      welcome: "Bienvenido a sQawk",
      joinCommunity: "Únete a la comunidad global",
      login: "Iniciar Sesión",
      signUp: "Registrarse",
      signIn: "Entrar",
      signingIn: "Entrando...",
      createAccount: "Crear Cuenta",
      creatingAccount: "Creando cuenta...",
      name: "Nombre",
      username: "Usuario",
      email: "Correo",
      password: "Contraseña",
      city: "Ciudad",
      state: "Estado",
      country: "País",
      optional: "opc",
      dataSecure: "Tus datos están encriptados y seguros",
      signUpGoogle: "Registrarse con Google",
      signInGoogle: "Entrar con Google",
      orRegister: "O REGISTRARSE CON EMAIL",
      orLogin: "O ENTRAR CON EMAIL",
      findTravel: "Encuentra Amigos Viajeros",
      connectTravelers: "Conecta con otros viajeros",
      collectCities: "Colecciona Ciudades",
      buildFootprint: "Construye tu huella global",
      goLive: "En Vivo",
      broadcastWorld: "Transmite al mundo",
      promoteYourself: "Promuévete",
      getDiscovered: "Sé descubierto mundialmente",
      shareBusiness: "Comparte Tu Negocio",
      showcaseBrand: "Muestra tu marca y servicios",
      createProfile: "Crea tu Perfil sQawk Hoy",
      joinGlobal: "Únete a la comunidad global y comienza a conectar",
      missionP1: "sQawk es una plataforma social de nueva generación inspirada en lo que funcionó en los primeros días de las redes sociales — antes de que el engagement fuera diseñado y la atención explotada.",
      missionP2Intro: "Creemos:",
      missionP2: "La conversación supera la fama. La comunidad supera la viralidad. La autenticidad supera los algoritmos.",
      missionP3Start: "Esto no es redes sociales para influencers.",
      missionP3End: "Es redes sociales para personas.",
      alreadyHaveAccount: "¿Ya tienes una cuenta?",
      // New translations
      shareAnthem: "Comparte Tu Himno",
      showcaseMusic: "Muestra tu gusto musical",
      top5Squad: "Top 5 del Grupo",
      showcaseClosestFriends: "Muestra a tus amigos más cercanos",
      claimYourCity: "Reclama Tu Ciudad",
      searchClaimCity: "¡Busca y Reclama Tu Ciudad!",
      globalBusinessDirectory: "Directorio Global $1",
      listBusinessWorld: "¡Lista tu negocio al mundo por solo $1!",
      liveCounter: "Contador en Vivo",
      liveUsers: "Usuarios en Vivo",
      streamingNow: "Transmitiendo Ahora",
      // Additional landing page translations
      beFoundingMember: "¡Sé el miembro fundador de la comunidad sQawk de tu ciudad!",
      startConversations: "Inicia conversaciones, comparte noticias locales y conecta con personas de tu ciudad.",
      freeToClaim: "Gratis para reclamar • Conviértete en miembro fundador • Construye tu comunidad local",
      bePartOfGlobal: "¡Sé Parte de un Experimento GLOBAL! Nuestro objetivo es albergar",
      businessesWorldwide: "negocios en todo el mundo.",
      payJust1Dollar: "¡Paga solo $1 y tu negocio aparecerá en nuestro ticker en vivo y directorio!",
      clickPayAddBusiness: "¡Haz Clic Aquí para Pagar $1 y Añadir Tu Negocio!",
      noAccountRequired: "Sin cuenta requerida • Listado instantáneo • Alcance global",
      spotsRemaining: "Lugares Restantes",
      outOf100k: "de 100,000",
      joinedCount: "unidos",
      goalText: "Meta: 100K"
    },
    fr: {
      tagline: "sQawk À Travers le Monde.",
      subtitle: "Une plateforme sociale où la géographie rencontre la mélodie. Collectionnez des villes, créez votre équipe et diffusez votre hymne au monde.",
      getStarted: "Commencer",
      secure: "Sécurisé",
      joinsQawk: "Rejoins sQawk!",
      welcome: "Bienvenue sur sQawk",
      joinCommunity: "Rejoignez la communauté mondiale",
      login: "Connexion",
      signUp: "S'inscrire",
      signIn: "Se Connecter",
      signingIn: "Connexion...",
      createAccount: "Créer un Compte",
      creatingAccount: "Création du compte...",
      name: "Nom",
      username: "Pseudo",
      email: "Email",
      password: "Mot de passe",
      city: "Ville",
      state: "État",
      country: "Pays",
      optional: "opt",
      dataSecure: "Vos données sont cryptées et sécurisées",
      signUpGoogle: "S'inscrire avec Google",
      signInGoogle: "Se connecter avec Google",
      orRegister: "OU S'INSCRIRE PAR EMAIL",
      orLogin: "OU SE CONNECTER PAR EMAIL",
      findTravel: "Trouver des Amis Voyageurs",
      connectTravelers: "Connectez-vous avec d'autres voyageurs",
      collectCities: "Collectionnez des Villes",
      buildFootprint: "Construisez votre empreinte mondiale",
      goLive: "En Direct",
      broadcastWorld: "Diffusez au monde",
      promoteYourself: "Promouvez-vous",
      getDiscovered: "Soyez découvert mondialement",
      shareBusiness: "Partagez Votre Business",
      showcaseBrand: "Présentez votre marque et services",
      createProfile: "Créez votre Profil sQawk Aujourd'hui",
      joinGlobal: "Rejoignez la communauté mondiale et commencez à connecter",
      missionP1: "sQawk est une plateforme sociale nouvelle génération inspirée de ce qui fonctionnait aux débuts des réseaux sociaux — avant que l'engagement soit conçu et l'attention exploitée.",
      missionP2Intro: "Nous croyons:",
      missionP2: "La conversation bat la notoriété. La communauté bat la viralité. L'authenticité bat les algorithmes.",
      missionP3Start: "Ce n'est pas un réseau social pour les influenceurs.",
      missionP3End: "C'est un réseau social pour les gens.",
      alreadyHaveAccount: "Vous avez déjà un compte?",
      shareAnthem: "Partagez Votre Hymne",
      showcaseMusic: "Montrez vos goûts musicaux",
      top5Squad: "Top 5 de l'Équipe",
      showcaseClosestFriends: "Montrez vos amis les plus proches",
      claimYourCity: "Réclamez Votre Ville",
      searchClaimCity: "Recherchez et Réclamez Votre Ville!",
      globalBusinessDirectory: "Annuaire Global 1$",
      listBusinessWorld: "Listez votre entreprise au monde pour seulement 1$!",
      liveCounter: "Compteur en Direct",
      liveUsers: "Utilisateurs en Direct",
      streamingNow: "En Streaming",
      beFoundingMember: "Soyez le membre fondateur de la communauté sQawk de votre ville!",
      startConversations: "Lancez des conversations, partagez les actualités locales et connectez-vous avec les gens de votre ville.",
      freeToClaim: "Gratuit • Devenez membre fondateur • Construisez votre communauté locale",
      bePartOfGlobal: "Participez à une Expérience MONDIALE! Notre objectif est d'héberger",
      businessesWorldwide: "entreprises dans le monde.",
      clickPayAddBusiness: "Cliquez Ici pour Payer 1$ et Ajouter Votre Entreprise!",
      noAccountRequired: "Pas de compte requis • Inscription instantanée • Portée mondiale",
      spotsRemaining: "Places Restantes",
      outOf100k: "sur 100 000",
      joinedCount: "inscrits",
      goalText: "Objectif: 100K"
    },
    zh: {
      tagline: "sQawk 遍布全球",
      subtitle: "一个地理与旋律相遇的社交平台。收集城市，建立你的团队，向世界广播你的声音。",
      getStarted: "开始",
      secure: "安全",
      joinsQawk: "加入 sQawk!",
      welcome: "欢迎来到 sQawk",
      joinCommunity: "加入全球社区",
      login: "登录",
      signUp: "注册",
      signIn: "登录",
      signingIn: "登录中...",
      createAccount: "创建账户",
      creatingAccount: "创建账户中...",
      name: "姓名",
      username: "用户名",
      email: "邮箱",
      password: "密码",
      city: "城市",
      state: "省份",
      country: "国家",
      optional: "选填",
      dataSecure: "您的数据已加密并安全",
      signUpGoogle: "使用谷歌注册",
      signInGoogle: "使用谷歌登录",
      orRegister: "或使用邮箱注册",
      orLogin: "或使用邮箱登录",
      findTravel: "寻找旅伴",
      connectTravelers: "与其他旅行者联系",
      collectCities: "收集城市",
      buildFootprint: "建立您的全球足迹",
      goLive: "直播",
      broadcastWorld: "向世界广播",
      promoteYourself: "推广自己",
      getDiscovered: "被全球发现",
      shareBusiness: "分享您的业务",
      showcaseBrand: "展示您的品牌和服务",
      createProfile: "今天创建您的 sQawk 个人资料",
      joinGlobal: "加入全球社区并开始连接",
      missionP1: "sQawk 是一个新一代社交平台，灵感来自于社交媒体早期的成功——在参与度被设计、注意力被利用之前。",
      missionP2Intro: "我们相信：",
      missionP2: "对话胜过影响力。社区胜过病毒传播。真实胜过算法。",
      missionP3Start: "这不是网红的社交媒体。",
      missionP3End: "这是普通人的社交媒体。",
      alreadyHaveAccount: "已有账户？",
      shareAnthem: "分享你的主题曲",
      showcaseMusic: "展示你的音乐品味",
      top5Squad: "前5好友",
      showcaseClosestFriends: "展示你最亲密的朋友",
      claimYourCity: "认领你的城市",
      searchClaimCity: "搜索并认领你的城市!",
      globalBusinessDirectory: "1美元全球商业目录",
      listBusinessWorld: "只需1美元即可将您的企业列入全球目录!",
      liveCounter: "直播计数器",
      liveUsers: "在线用户",
      streamingNow: "正在直播",
      beFoundingMember: "成为您家乡sQawk社区的创始成员!",
      startConversations: "开始对话，分享本地新闻，与您城市的人联系。",
      freeToClaim: "免费认领 • 成为创始成员 • 建立您的本地社区",
      bePartOfGlobal: "参与全球实验！我们的目标是托管",
      businessesWorldwide: "家全球企业。",
      clickPayAddBusiness: "点击这里支付1美元并添加您的业务！",
      noAccountRequired: "无需账户 • 即时上架 • 全球覆盖",
      spotsRemaining: "剩余名额",
      outOf100k: "共100,000",
      joinedCount: "已加入",
      goalText: "目标: 100K"
    },
    de: {
      tagline: "sQawk Rund um die Welt.",
      subtitle: "Eine standortbasierte soziale Plattform, wo Geographie auf Melodie trifft. Sammle Städte, kuratiere dein Team und sende deine Hymne in die Welt.",
      getStarted: "Loslegen",
      secure: "Sicher",
      joinsQawk: "Tritt sQawk bei!",
      welcome: "Willkommen bei sQawk",
      joinCommunity: "Tritt der globalen Gemeinschaft bei",
      login: "Anmelden",
      signUp: "Registrieren",
      signIn: "Einloggen",
      signingIn: "Einloggen...",
      createAccount: "Konto Erstellen",
      creatingAccount: "Konto wird erstellt...",
      name: "Name",
      username: "Benutzername",
      email: "E-Mail",
      password: "Passwort",
      city: "Stadt",
      state: "Bundesland",
      country: "Land",
      optional: "opt",
      dataSecure: "Ihre Daten sind verschlüsselt und sicher",
      signUpGoogle: "Mit Google registrieren",
      signInGoogle: "Mit Google anmelden",
      orRegister: "ODER MIT E-MAIL REGISTRIEREN",
      orLogin: "ODER MIT E-MAIL ANMELDEN",
      findTravel: "Reisefreunde Finden",
      connectTravelers: "Verbinde dich mit anderen Reisenden",
      collectCities: "Städte Sammeln",
      buildFootprint: "Baue deinen globalen Fußabdruck auf",
      goLive: "Live Gehen",
      broadcastWorld: "Sende in die Welt",
      promoteYourself: "Promote Dich Selbst",
      getDiscovered: "Werde weltweit entdeckt",
      shareBusiness: "Teile Dein Geschäft",
      showcaseBrand: "Präsentiere deine Marke & Dienste",
      createProfile: "Erstelle heute dein sQawk Profil",
      joinGlobal: "Tritt der globalen Gemeinschaft bei und beginne zu verbinden",
      missionP1: "sQawk ist eine Social-Media-Plattform der nächsten Generation, inspiriert von dem, was in den frühen Tagen der sozialen Medien funktionierte — bevor Engagement entwickelt und Aufmerksamkeit ausgenutzt wurde.",
      missionP2Intro: "Wir glauben:",
      missionP2: "Gespräch schlägt Einfluss. Gemeinschaft schlägt Viralität. Authentizität schlägt Algorithmen.",
      missionP3Start: "Das ist kein Social Media für Influencer.",
      missionP3End: "Es ist Social Media für Menschen.",
      alreadyHaveAccount: "Hast du schon ein Konto?",
      shareAnthem: "Teile Deine Hymne",
      showcaseMusic: "Zeige deinen Musikgeschmack",
      top5Squad: "Top 5 Team",
      showcaseClosestFriends: "Zeige deine engsten Freunde",
      claimYourCity: "Beanspruche Deine Stadt",
      searchClaimCity: "Suche und Beanspruche Deine Stadt!",
      globalBusinessDirectory: "1€ Globales Branchenverzeichnis",
      listBusinessWorld: "Liste dein Geschäft weltweit für nur 1€!",
      liveCounter: "Live-Zähler",
      liveUsers: "Live Benutzer",
      streamingNow: "Jetzt Streamen",
      beFoundingMember: "Sei das Gründungsmitglied der sQawk-Community deiner Heimatstadt!",
      startConversations: "Starte Gespräche, teile lokale Nachrichten und verbinde dich mit Menschen in deiner Stadt.",
      freeToClaim: "Kostenlos beanspruchen • Werde Gründungsmitglied • Baue deine lokale Community auf",
      bePartOfGlobal: "Sei Teil eines GLOBALEN Experiments! Unser Ziel ist es,",
      businessesWorldwide: "Unternehmen weltweit zu hosten.",
      spotsRemaining: "Plätze Verfügbar",
      outOf100k: "von 100.000",
      clickPayAddBusiness: "Klicken Sie hier, um 1€ zu zahlen und Ihr Geschäft hinzuzufügen!",
      noAccountRequired: "Kein Konto erforderlich • Sofortige Listung • Globale Reichweite",
      joinedCount: "beigetreten",
      goalText: "Ziel: 100K"
    },
    it: {
      tagline: "sQawk In Tutto il Mondo.",
      subtitle: "Una piattaforma sociale dove la geografia incontra la melodia. Colleziona città, crea il tuo gruppo e trasmetti il tuo inno al mondo.",
      getStarted: "Inizia",
      secure: "Sicuro",
      joinsQawk: "Unisciti a sQawk!",
      welcome: "Benvenuto su sQawk",
      joinCommunity: "Unisciti alla comunità globale",
      login: "Accedi",
      signUp: "Registrati",
      signIn: "Accedi",
      signingIn: "Accesso...",
      createAccount: "Crea Account",
      creatingAccount: "Creazione account...",
      name: "Nome",
      username: "Username",
      email: "Email",
      password: "Password",
      city: "Città",
      state: "Regione",
      country: "Paese",
      optional: "opz",
      dataSecure: "I tuoi dati sono crittografati e sicuri",
      signUpGoogle: "Registrati con Google",
      signInGoogle: "Accedi con Google",
      orRegister: "O REGISTRATI CON EMAIL",
      orLogin: "O ACCEDI CON EMAIL",
      findTravel: "Trova Amici di Viaggio",
      connectTravelers: "Connettiti con altri viaggiatori",
      collectCities: "Colleziona Città",
      buildFootprint: "Costruisci la tua impronta globale",
      goLive: "Vai in Diretta",
      broadcastWorld: "Trasmetti al mondo",
      promoteYourself: "Promuovi Te Stesso",
      getDiscovered: "Fatti scoprire nel mondo",
      shareBusiness: "Condividi la Tua Attività",
      showcaseBrand: "Mostra il tuo brand e servizi",
      createProfile: "Crea il tuo Profilo sQawk Oggi",
      joinGlobal: "Unisciti alla comunità globale e inizia a connetterti",
      missionP1: "sQawk è una piattaforma social di nuova generazione ispirata a ciò che funzionava nei primi giorni dei social media — prima che l'engagement fosse ingegnerizzato e l'attenzione sfruttata.",
      missionP2Intro: "Crediamo:",
      missionP2: "La conversazione batte la fama. La comunità batte la viralità. L'autenticità batte gli algoritmi.",
      missionP3Start: "Questo non è social media per influencer.",
      missionP3End: "È social media per le persone.",
      alreadyHaveAccount: "Hai già un account?",
      shareAnthem: "Condividi Il Tuo Inno",
      showcaseMusic: "Mostra i tuoi gusti musicali",
      top5Squad: "Top 5 del Gruppo",
      showcaseClosestFriends: "Mostra i tuoi amici più cari",
      claimYourCity: "Rivendica La Tua Città",
      searchClaimCity: "Cerca e Rivendica La Tua Città!",
      globalBusinessDirectory: "Directory Globale 1€",
      listBusinessWorld: "Elenca la tua attività nel mondo per solo 1€!",
      liveCounter: "Contatore Live",
      liveUsers: "Utenti Live",
      streamingNow: "In Diretta Ora",
      beFoundingMember: "Diventa il membro fondatore della comunità sQawk della tua città!",
      startConversations: "Avvia conversazioni, condividi notizie locali e connettiti con le persone della tua città.",
      freeToClaim: "Gratuito • Diventa membro fondatore • Costruisci la tua comunità locale",
      bePartOfGlobal: "Fai Parte di un Esperimento GLOBALE! Il nostro obiettivo è ospitare",
      businessesWorldwide: "aziende in tutto il mondo.",
      spotsRemaining: "Posti Rimanenti",
      clickPayAddBusiness: "Clicca Qui per Pagare 1€ e Aggiungere la Tua Attività!",
      noAccountRequired: "Nessun account richiesto • Inserimento istantaneo • Portata globale",
      outOf100k: "su 100.000",
      joinedCount: "iscritti",
      goalText: "Obiettivo: 100K"
    },
    hi: {
      tagline: "sQawk पूरी दुनिया में",
      subtitle: "एक स्थान-आधारित सोशल प्लेटफॉर्म जहां भूगोल संगीत से मिलता है। शहर इकट्ठा करें, अपनी टीम बनाएं और अपना गान दुनिया को प्रसारित करें।",
      getStarted: "शुरू करें",
      secure: "सुरक्षित",
      joinsQawk: "sQawk से जुड़ें!",
      welcome: "sQawk में आपका स्वागत है",
      joinCommunity: "वैश्विक समुदाय से जुड़ें",
      login: "लॉगिन",
      signUp: "साइन अप",
      signIn: "साइन इन",
      signingIn: "साइन इन हो रहा है...",
      createAccount: "खाता बनाएं",
      creatingAccount: "खाता बन रहा है...",
      name: "नाम",
      username: "यूजरनेम",
      email: "ईमेल",
      password: "पासवर्ड",
      city: "शहर",
      state: "राज्य",
      country: "देश",
      optional: "वैकल्पिक",
      dataSecure: "आपका डेटा एन्क्रिप्टेड और सुरक्षित है",
      signUpGoogle: "Google से साइन अप करें",
      signInGoogle: "Google से साइन इन करें",
      orRegister: "या ईमेल से रजिस्टर करें",
      orLogin: "या ईमेल से लॉगिन करें",
      findTravel: "यात्रा मित्र खोजें",
      connectTravelers: "अन्य यात्रियों से जुड़ें",
      collectCities: "शहर इकट्ठा करें",
      buildFootprint: "अपनी वैश्विक छाप बनाएं",
      goLive: "लाइव जाएं",
      broadcastWorld: "दुनिया को प्रसारित करें",
      promoteYourself: "खुद को प्रमोट करें",
      getDiscovered: "विश्व स्तर पर खोजे जाएं",
      shareBusiness: "अपना व्यवसाय साझा करें",
      showcaseBrand: "अपने ब्रांड और सेवाओं को प्रदर्शित करें",
      createProfile: "आज अपना sQawk प्रोफाइल बनाएं",
      joinGlobal: "वैश्विक समुदाय से जुड़ें और कनेक्ट करना शुरू करें",
      missionP1: "sQawk एक नई पीढ़ी का सोशल प्लेटफॉर्म है जो सोशल मीडिया के शुरुआती दिनों से प्रेरित है — जब एंगेजमेंट इंजीनियर नहीं था और ध्यान का शोषण नहीं होता था।",
      missionP2Intro: "हम मानते हैं:",
      missionP2: "बातचीत प्रसिद्धि को मात देती है। समुदाय वायरलिटी को मात देता है। प्रामाणिकता एल्गोरिदम को मात देती है।",
      missionP3Start: "यह इन्फ्लुएंसर्स के लिए सोशल मीडिया नहीं है।",
      missionP3End: "यह लोगों के लिए सोशल मीडिया है।",
      alreadyHaveAccount: "पहले से खाता है?",
      shareAnthem: "अपना गीत साझा करें",
      showcaseMusic: "अपना संगीत स्वाद दिखाएं",
      top5Squad: "टॉप 5 टीम",
      showcaseClosestFriends: "अपने करीबी दोस्तों को दिखाएं",
      claimYourCity: "अपने शहर पर दावा करें",
      searchClaimCity: "खोजें और अपने शहर पर दावा करें!",
      globalBusinessDirectory: "₹80 वैश्विक व्यापार निर्देशिका",
      listBusinessWorld: "अपने व्यवसाय को केवल ₹80 में दुनिया में सूचीबद्ध करें!",
      liveCounter: "लाइव काउंटर",
      liveUsers: "लाइव उपयोगकर्ता",
      streamingNow: "अभी स्ट्रीमिंग",
      beFoundingMember: "अपने गृहनगर के sQawk समुदाय के संस्थापक सदस्य बनें!",
      startConversations: "बातचीत शुरू करें, स्थानीय समाचार साझा करें और अपने शहर के लोगों से जुड़ें।",
      freeToClaim: "मुफ्त में दावा करें • संस्थापक सदस्य बनें • अपना स्थानीय समुदाय बनाएं",
      bePartOfGlobal: "एक वैश्विक प्रयोग का हिस्सा बनें! हमारा लक्ष्य है",
      businessesWorldwide: "दुनिया भर में व्यवसायों को होस्ट करना।",
      spotsRemaining: "शेष स्थान",
      outOf100k: "100,000 में से",
      joinedCount: "शामिल हुए",
      clickPayAddBusiness: "यहां क्लिक करें ₹80 भुगतान करें और अपना व्यवसाय जोड़ें!",
      noAccountRequired: "कोई खाता आवश्यक नहीं • तत्काल सूचीबद्ध • वैश्विक पहुंच",
      goalText: "लक्ष्य: 100K"
    },
    pt: {
      tagline: "sQawk Pelo Mundo.",
      subtitle: "Uma plataforma social onde a geografia encontra a melodia. Colecione cidades, monte seu time e transmita seu hino para o mundo.",
      getStarted: "Começar",
      secure: "Seguro",
      joinsQawk: "Junte-se ao sQawk!",
      welcome: "Bem-vindo ao sQawk",
      joinCommunity: "Junte-se à comunidade global",
      login: "Login",
      signUp: "Cadastrar",
      signIn: "Entrar",
      signingIn: "Entrando...",
      createAccount: "Criar Conta",
      creatingAccount: "Criando conta...",
      name: "Nome",
      username: "Usuário",
      email: "E-mail",
      password: "Senha",
      city: "Cidade",
      state: "Estado",
      country: "País",
      optional: "opc",
      dataSecure: "Seus dados estão criptografados e seguros",
      signUpGoogle: "Cadastrar com Google",
      signInGoogle: "Entrar com Google",
      orRegister: "OU CADASTRE COM E-MAIL",
      orLogin: "OU ENTRE COM E-MAIL",
      findTravel: "Encontrar Amigos de Viagem",
      connectTravelers: "Conecte-se com outros viajantes",
      collectCities: "Colecione Cidades",
      buildFootprint: "Construa sua presença global",
      goLive: "Ao Vivo",
      broadcastWorld: "Transmita para o mundo",
      promoteYourself: "Promova-se",
      getDiscovered: "Seja descoberto mundialmente",
      shareBusiness: "Compartilhe Seu Negócio",
      showcaseBrand: "Mostre sua marca e serviços",
      createProfile: "Crie seu Perfil sQawk Hoje",
      joinGlobal: "Junte-se à comunidade global e comece a se conectar",
      missionP1: "sQawk é uma plataforma social de nova geração inspirada no que funcionou nos primeiros dias das redes sociais — antes que o engajamento fosse projetado e a atenção explorada.",
      missionP2Intro: "Acreditamos:",
      missionP2: "Conversa supera fama. Comunidade supera viralidade. Autenticidade supera algoritmos.",
      missionP3Start: "Isso não é mídia social para influenciadores.",
      missionP3End: "É mídia social para pessoas.",
      alreadyHaveAccount: "Já tem uma conta?",
      shareAnthem: "Compartilhe Seu Hino",
      showcaseMusic: "Mostre seu gosto musical",
      top5Squad: "Top 5 do Time",
      showcaseClosestFriends: "Mostre seus amigos mais próximos",
      claimYourCity: "Reivindique Sua Cidade",
      searchClaimCity: "Pesquise e Reivindique Sua Cidade!",
      globalBusinessDirectory: "Diretório Global R$5",
      listBusinessWorld: "Liste seu negócio para o mundo por apenas R$5!",
      liveCounter: "Contador ao Vivo",
      liveUsers: "Usuários ao Vivo",
      streamingNow: "Transmitindo Agora",
      beFoundingMember: "Seja o membro fundador da comunidade sQawk da sua cidade!",
      startConversations: "Inicie conversas, compartilhe notícias locais e conecte-se com pessoas da sua cidade.",
      freeToClaim: "Gratuito • Torne-se membro fundador • Construa sua comunidade local",
      bePartOfGlobal: "Faça Parte de um Experimento GLOBAL! Nosso objetivo é hospedar",
      businessesWorldwide: "empresas em todo o mundo.",
      clickPayAddBusiness: "Clique Aqui para Pagar R$5 e Adicionar Seu Negócio!",
      noAccountRequired: "Sem conta necessária • Listagem instantânea • Alcance global",
      spotsRemaining: "Vagas Restantes",
      outOf100k: "de 100.000",
      joinedCount: "inscritos",
      goalText: "Meta: 100K"
    },
    ar: {
      tagline: "sQawk حول العالم.",
      subtitle: "منصة اجتماعية حيث تلتقي الجغرافيا بالموسيقى. اجمع المدن، وأنشئ فريقك، وبث نشيدك للعالم.",
      getStarted: "ابدأ",
      secure: "آمن",
      joinsQawk: "انضم إلى sQawk!",
      welcome: "مرحباً بك في sQawk",
      joinCommunity: "انضم إلى المجتمع العالمي",
      login: "تسجيل الدخول",
      signUp: "إنشاء حساب",
      signIn: "دخول",
      signingIn: "جاري الدخول...",
      createAccount: "إنشاء حساب",
      creatingAccount: "جاري إنشاء الحساب...",
      name: "الاسم",
      username: "اسم المستخدم",
      email: "البريد الإلكتروني",
      password: "كلمة المرور",
      city: "المدينة",
      state: "المنطقة",
      country: "البلد",
      optional: "اختياري",
      dataSecure: "بياناتك مشفرة وآمنة",
      signUpGoogle: "التسجيل بواسطة Google",
      signInGoogle: "الدخول بواسطة Google",
      orRegister: "أو سجل بالبريد الإلكتروني",
      orLogin: "أو ادخل بالبريد الإلكتروني",
      findTravel: "ابحث عن أصدقاء السفر",
      connectTravelers: "تواصل مع مسافرين آخرين",
      collectCities: "اجمع المدن",
      buildFootprint: "ابنِ بصمتك العالمية",
      goLive: "بث مباشر",
      broadcastWorld: "بث للعالم",
      promoteYourself: "روّج لنفسك",
      getDiscovered: "اجعل العالم يكتشفك",
      shareBusiness: "شارك عملك",
      showcaseBrand: "اعرض علامتك التجارية وخدماتك",
      createProfile: "أنشئ ملفك الشخصي في sQawk اليوم",
      joinGlobal: "انضم إلى المجتمع العالمي وابدأ التواصل",
      missionP1: "sQawk منصة اجتماعية من الجيل الجديد مستوحاة مما نجح في الأيام الأولى لوسائل التواصل الاجتماعي — قبل أن يُهندس التفاعل ويُستغل الانتباه.",
      missionP2Intro: "نؤمن بأن:",
      missionP2: "المحادثة تتفوق على الشهرة. المجتمع يتفوق على الانتشار. الأصالة تتفوق على الخوارزميات.",
      missionP3Start: "هذا ليس وسائل تواصل للمؤثرين.",
      missionP3End: "إنه وسائل تواصل للناس.",
      alreadyHaveAccount: "لديك حساب بالفعل؟",
      shareAnthem: "شارك نشيدك",
      showcaseMusic: "اعرض ذوقك الموسيقي",
      top5Squad: "أفضل 5 أصدقاء",
      showcaseClosestFriends: "اعرض أقرب أصدقائك",
      claimYourCity: "طالب بمدينتك",
      searchClaimCity: "ابحث وطالب بمدينتك!",
      globalBusinessDirectory: "دليل الأعمال العالمي $1",
      listBusinessWorld: "أدرج عملك للعالم مقابل $1 فقط!",
      liveCounter: "عداد البث المباشر",
      liveUsers: "المستخدمون المباشرون",
      streamingNow: "يبث الآن",
      beFoundingMember: "كن العضو المؤسس لمجتمع sQawk في مدينتك!",
      startConversations: "ابدأ المحادثات، شارك الأخبار المحلية، وتواصل مع الناس في مدينتك.",
      freeToClaim: "مجاني • كن عضواً مؤسساً • ابنِ مجتمعك المحلي",
      bePartOfGlobal: "كن جزءاً من تجربة عالمية! هدفنا استضافة",
      businessesWorldwide: "شركة حول العالم.",
      clickPayAddBusiness: "انقر هنا لدفع $1 وإضافة عملك!",
      noAccountRequired: "لا يلزم حساب • إدراج فوري • وصول عالمي",
      spotsRemaining: "الأماكن المتبقية",
      outOf100k: "من 100,000",
      joinedCount: "انضموا",
      goalText: "الهدف: 100K"
    },
    ja: {
      tagline: "sQawk 世界中で。",
      subtitle: "地理とメロディーが出会うソーシャルプラットフォーム。都市を集め、チームを作り、あなたの声を世界に届けよう。",
      getStarted: "始める",
      secure: "安全",
      joinsQawk: "sQawkに参加しよう！",
      welcome: "sQawkへようこそ",
      joinCommunity: "グローバルコミュニティに参加",
      login: "ログイン",
      signUp: "新規登録",
      signIn: "サインイン",
      signingIn: "サインイン中...",
      createAccount: "アカウント作成",
      creatingAccount: "アカウント作成中...",
      name: "名前",
      username: "ユーザー名",
      email: "メール",
      password: "パスワード",
      city: "都市",
      state: "都道府県",
      country: "国",
      optional: "任意",
      dataSecure: "データは暗号化され安全です",
      signUpGoogle: "Googleで登録",
      signInGoogle: "Googleでサインイン",
      orRegister: "またはメールで登録",
      orLogin: "またはメールでログイン",
      findTravel: "旅行仲間を見つける",
      connectTravelers: "他の旅行者とつながる",
      collectCities: "都市を集める",
      buildFootprint: "グローバルな足跡を築く",
      goLive: "ライブ配信",
      broadcastWorld: "世界に配信",
      promoteYourself: "自分をアピール",
      getDiscovered: "世界に発見される",
      shareBusiness: "ビジネスを共有",
      showcaseBrand: "ブランドとサービスを紹介",
      createProfile: "今日sQawkプロフィールを作成",
      joinGlobal: "グローバルコミュニティに参加してつながりを始めよう",
      missionP1: "sQawkは、ソーシャルメディアの初期に成功したことに触発された次世代のソーシャルプラットフォームです — エンゲージメントが設計され、注目が搾取される前の時代です。",
      missionP2Intro: "私たちは信じています：",
      missionP2: "会話は名声に勝る。コミュニティはバイラルに勝る。本物はアルゴリズムに勝る。",
      missionP3Start: "これはインフルエンサーのためのソーシャルメディアではありません。",
      missionP3End: "人々のためのソーシャルメディアです。",
      alreadyHaveAccount: "すでにアカウントをお持ちですか？",
      shareAnthem: "あなたのアンセムを共有",
      showcaseMusic: "音楽の好みを紹介",
      top5Squad: "トップ5スクワッド",
      showcaseClosestFriends: "親しい友人を紹介",
      claimYourCity: "あなたの都市を主張",
      searchClaimCity: "検索してあなたの都市を主張！",
      globalBusinessDirectory: "¥150グローバルビジネスディレクトリ",
      listBusinessWorld: "たった¥150であなたのビジネスを世界に掲載！",
      liveCounter: "ライブカウンター",
      liveUsers: "ライブユーザー",
      streamingNow: "配信中",
      beFoundingMember: "あなたの街のsQawkコミュニティの創設メンバーになろう！",
      startConversations: "会話を始め、地元のニュースを共有し、あなたの街の人々とつながりましょう。",
      freeToClaim: "無料で主張 • 創設メンバーになる • 地元コミュニティを構築",
      bePartOfGlobal: "グローバル実験に参加しよう！私たちの目標は",
      businessesWorldwide: "の企業を世界中でホストすること。",
      spotsRemaining: "残り枠",
      outOf100k: "100,000のうち",
      clickPayAddBusiness: "ここをクリックして¥150を支払い、ビジネスを追加！",
      noAccountRequired: "アカウント不要 • 即時掲載 • グローバルリーチ",
      joinedCount: "参加済み",
      goalText: "目標: 100K"
    },
    ko: {
      tagline: "sQawk 전 세계로.",
      subtitle: "지리와 멜로디가 만나는 소셜 플랫폼. 도시를 수집하고, 팀을 만들고, 당신의 목소리를 세계에 전하세요.",
      getStarted: "시작하기",
      secure: "안전함",
      joinsQawk: "sQawk 가입하기!",
      welcome: "sQawk에 오신 것을 환영합니다",
      joinCommunity: "글로벌 커뮤니티에 참여하세요",
      login: "로그인",
      signUp: "회원가입",
      signIn: "로그인",
      signingIn: "로그인 중...",
      createAccount: "계정 만들기",
      creatingAccount: "계정 생성 중...",
      name: "이름",
      username: "사용자명",
      email: "이메일",
      password: "비밀번호",
      city: "도시",
      state: "시/도",
      country: "국가",
      optional: "선택",
      dataSecure: "데이터는 암호화되어 안전합니다",
      signUpGoogle: "Google로 가입",
      signInGoogle: "Google로 로그인",
      orRegister: "또는 이메일로 가입",
      orLogin: "또는 이메일로 로그인",
      findTravel: "여행 친구 찾기",
      connectTravelers: "다른 여행자들과 연결하세요",
      collectCities: "도시 수집하기",
      buildFootprint: "글로벌 발자국을 남기세요",
      goLive: "라이브 방송",
      broadcastWorld: "세계에 방송하세요",
      promoteYourself: "자신을 홍보하세요",
      getDiscovered: "전 세계에서 발견되세요",
      shareBusiness: "비즈니스 공유",
      showcaseBrand: "브랜드와 서비스를 소개하세요",
      createProfile: "오늘 sQawk 프로필을 만드세요",
      joinGlobal: "글로벌 커뮤니티에 참여하고 연결을 시작하세요",
      missionP1: "sQawk는 소셜 미디어 초창기의 성공에서 영감을 받은 차세대 소셜 플랫폼입니다 — 참여가 설계되고 관심이 착취되기 전의 시대입니다.",
      missionP2Intro: "우리는 믿습니다:",
      missionP2: "대화가 명성을 이깁니다. 커뮤니티가 바이럴을 이깁니다. 진정성이 알고리즘을 이깁니다.",
      missionP3Start: "이것은 인플루언서를 위한 소셜 미디어가 아닙니다.",
      missionP3End: "사람들을 위한 소셜 미디어입니다.",
      alreadyHaveAccount: "이미 계정이 있으신가요?",
      shareAnthem: "당신의 앤섬 공유",
      showcaseMusic: "음악 취향 소개",
      top5Squad: "Top 5 스쿼드",
      showcaseClosestFriends: "친한 친구 소개",
      claimYourCity: "당신의 도시를 주장하세요",
      searchClaimCity: "검색하고 당신의 도시를 주장하세요!",
      globalBusinessDirectory: "₩1,300 글로벌 비즈니스 디렉토리",
      listBusinessWorld: "단 ₩1,300에 비즈니스를 세계에 등록하세요!",
      liveCounter: "라이브 카운터",
      liveUsers: "라이브 사용자",
      streamingNow: "스트리밍 중",
      beFoundingMember: "당신의 고향 sQawk 커뮤니티의 창립 멤버가 되세요!",
      startConversations: "대화를 시작하고, 지역 뉴스를 공유하고, 당신의 도시 사람들과 연결하세요.",
      freeToClaim: "무료로 주장 • 창립 멤버가 되기 • 지역 커뮤니티 구축",
      bePartOfGlobal: "글로벌 실험에 참여하세요! 우리의 목표는",
      businessesWorldwide: "개의 비즈니스를 전 세계에서 호스팅하는 것입니다.",
      spotsRemaining: "남은 자리",
      outOf100k: "100,000 중",
      clickPayAddBusiness: "여기를 클릭하여 ₩1,300을 지불하고 비즈니스를 추가하세요!",
      noAccountRequired: "계정 불필요 • 즉시 등록 • 글로벌 도달",
      joinedCount: "참여함",
      goalText: "목표: 100K"
    },
    ru: {
      tagline: "sQawk По Всему Миру.",
      subtitle: "Социальная платформа, где география встречается с мелодией. Собирайте города, создавайте свою команду и транслируйте свой гимн миру.",
      getStarted: "Начать",
      secure: "Безопасно",
      joinsQawk: "Присоединяйтесь к sQawk!",
      welcome: "Добро пожаловать в sQawk",
      joinCommunity: "Присоединяйтесь к глобальному сообществу",
      login: "Войти",
      signUp: "Регистрация",
      signIn: "Вход",
      signingIn: "Входим...",
      createAccount: "Создать аккаунт",
      creatingAccount: "Создание аккаунта...",
      name: "Имя",
      username: "Имя пользователя",
      email: "Эл. почта",
      password: "Пароль",
      city: "Город",
      state: "Область",
      country: "Страна",
      optional: "необ.",
      dataSecure: "Ваши данные зашифрованы и защищены",
      signUpGoogle: "Регистрация через Google",
      signInGoogle: "Войти через Google",
      orRegister: "ИЛИ ЗАРЕГИСТРИРУЙТЕСЬ ПО EMAIL",
      orLogin: "ИЛИ ВОЙДИТЕ ПО EMAIL",
      findTravel: "Найти попутчиков",
      connectTravelers: "Связывайтесь с другими путешественниками",
      collectCities: "Собирайте города",
      buildFootprint: "Создайте свой глобальный след",
      goLive: "Прямой эфир",
      broadcastWorld: "Транслируйте на весь мир",
      promoteYourself: "Продвигайте себя",
      getDiscovered: "Станьте известным во всем мире",
      shareBusiness: "Поделитесь бизнесом",
      showcaseBrand: "Продемонстрируйте свой бренд и услуги",
      createProfile: "Создайте свой профиль sQawk сегодня",
      joinGlobal: "Присоединяйтесь к глобальному сообществу и начните общаться",
      missionP1: "sQawk — это социальная платформа нового поколения, вдохновленная тем, что работало в ранние дни социальных сетей — до того, как вовлеченность стала инструментом, а внимание — эксплуатируемым ресурсом.",
      missionP2Intro: "Мы верим:",
      missionP2: "Общение важнее популярности. Сообщество важнее вирусности. Подлинность важнее алгоритмов.",
      missionP3Start: "Это не социальная сеть для инфлюенсеров.",
      missionP3End: "Это социальная сеть для людей.",
      alreadyHaveAccount: "Уже есть аккаунт?",
      shareAnthem: "Поделитесь своим гимном",
      showcaseMusic: "Покажите свой музыкальный вкус",
      top5Squad: "Топ 5 команда",
      showcaseClosestFriends: "Покажите близких друзей",
      claimYourCity: "Заявите о своем городе",
      searchClaimCity: "Найдите и заявите о своем городе!",
      globalBusinessDirectory: "$1 Глобальный каталог бизнеса",
      listBusinessWorld: "Разместите свой бизнес для всего мира всего за $1!",
      liveCounter: "Счетчик в реальном времени",
      liveUsers: "Пользователи онлайн",
      streamingNow: "Сейчас в эфире",
      beFoundingMember: "Станьте основателем сообщества sQawk в вашем родном городе!",
      startConversations: "Начинайте разговоры, делитесь местными новостями и общайтесь с людьми в вашем городе.",
      freeToClaim: "Бесплатно заявить • Станьте основателем • Создайте местное сообщество",
      bePartOfGlobal: "Станьте частью ГЛОБАЛЬНОГО эксперимента! Наша цель — разместить",
      businessesWorldwide: "бизнесов по всему миру.",
      spotsRemaining: "Мест осталось",
      outOf100k: "из 100 000",
      clickPayAddBusiness: "Нажмите здесь, чтобы заплатить $1 и добавить свой бизнес!",
      noAccountRequired: "Аккаунт не нужен • Мгновенное размещение • Глобальный охват",
      joinedCount: "присоединились",
      goalText: "Цель: 100K"
    }
  };
  
  // Memoize translations for performance - only recalculates when language changes
  const t = useMemo(() => translations[language] || translations.en, [language]);
  
  const languages = [
    { code: "en", name: "English", flag: "🇺🇸" },
    { code: "es", name: "Español", flag: "🇪🇸" },
    { code: "pt", name: "Português", flag: "🇧🇷" },
    { code: "fr", name: "Français", flag: "🇫🇷" },
    { code: "ru", name: "Русский", flag: "🇷🇺" },
    { code: "zh", name: "中文", flag: "🇨🇳" },
    { code: "de", name: "Deutsch", flag: "🇩🇪" },
    { code: "it", name: "Italiano", flag: "🇮🇹" },
    { code: "hi", name: "हिन्दी", flag: "🇮🇳" },
    { code: "ar", name: "العربية", flag: "🇸🇦" },
    { code: "ja", name: "日本語", flag: "🇯🇵" },
    { code: "ko", name: "한국어", flag: "🇰🇷" }
  ];

  // Email auth state
  const [authTab, setAuthTab] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("");
  const [travelingTo, setTravelingTo] = useState("");
  const [loading, setLoading] = useState(false);
  
  // reCAPTCHA state
  const [captchaConfig, setCaptchaConfig] = useState({ enabled: false, site_key: "", type: "v2" });
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaReady, setCaptchaReady] = useState(false);
  const recaptchaRef = useRef(null);
  
  // Age verification state
  const [birthDate, setBirthDate] = useState("");
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  
  // Shooting stars (ambient intergalactic effect — kept for atmosphere)
  const [shootingStars, setShootingStars] = useState([]);

  // Generate shooting stars periodically
  useEffect(() => {
    const createShootingStar = () => {
      const id = Date.now() + Math.random();
      setShootingStars(prev => [...prev, {
        id,
        top: Math.random() * 50 + "%",
        right: Math.random() * 30 + "%",
        delay: Math.random() * 0.5,
      }]);
      setTimeout(() => setShootingStars(prev => prev.filter(s => s.id !== id)), 2500);
    };
    const interval = setInterval(() => { if (Math.random() > 0.3) createShootingStar(); }, 2200);
    setTimeout(createShootingStar, 600);
    setTimeout(createShootingStar, 1800);
    return () => clearInterval(interval);
  }, []);

  // Scroll-triggered rocket — large, centered above the Mission Statement text.
  // Launches straight up as the user scrolls down. Reverses smoothly when scrolling back up.
  const { scrollYProgress } = useScroll();
  // Spring config tuned for Android Chrome smoothness: lower stiffness +
  // higher damping = no overshoot, no scroll-jank. Mass kept light so the
  // rocket still feels responsive.
  const smoothProgress = useSpring(scrollYProgress, { stiffness: 40, damping: 28, mass: 0.5, restDelta: 0.001 });
  // Vertical launch trajectory (no horizontal drift)
  const rocketY = useTransform(smoothProgress, [0, 0.08, 0.22], ["0%", "-60%", "-260%"]);
  // Slight wobble/tilt for cinematic launch — gentle sway
  const rocketRotate = useTransform(smoothProgress, [0, 0.1, 0.22], [-2, 2, -3]);
  // Grows slightly as engines ignite, then shrinks as it flies away
  const rocketScale = useTransform(smoothProgress, [0, 0.06, 0.22], [1, 1.08, 0.78]);
  // Smoke plume billows outward as it ignites, then dissipates
  const smokeScale = useTransform(smoothProgress, [0, 0.05, 0.18, 0.28], [1, 1.25, 1.5, 1.7]);
  const smokeOpacity = useTransform(smoothProgress, [0, 0.03, 0.18, 0.3], [0.55, 0.95, 0.5, 0]);
  // Rising-exhaust trail: a smoke column that fades in as the rocket lifts
  // off so it visibly trails behind the rocket during launch. Peaks while the
  // rocket is mid-flight, dissipates after it leaves the frame.
  const trailOpacity = useTransform(smoothProgress, [0, 0.04, 0.12, 0.22, 0.3], [0, 0.5, 0.95, 0.6, 0]);
  const trailScaleY = useTransform(smoothProgress, [0, 0.05, 0.18, 0.28], [0.2, 0.8, 1.4, 1.7]);
  // Rocket fades out as it leaves the atmosphere
  const rocketOpacity = useTransform(smoothProgress, [0, 0.18, 0.28], [1, 1, 0]);

  // === Hero ignition camera-shake (one-shot per session) ===
  // Triggers a brief, weighty shake on the hero the FIRST time the user scrolls
  // past the rocket's ignition threshold. Persists across the same browser tab
  // via sessionStorage so it only happens once per session.
  const heroShakeControls = useAnimation();
  const hasShakenRef = useRef(false);
  useEffect(() => {
    if (typeof window !== "undefined" && window.sessionStorage?.getItem("rocket_shaken") === "1") {
      hasShakenRef.current = true;
    }
  }, []);
  useMotionValueEvent(smoothProgress, "change", (v) => {
    if (hasShakenRef.current || v < 0.025) return;
    hasShakenRef.current = true;
    try { window.sessionStorage?.setItem("rocket_shaken", "1"); } catch (_) {}
    heroShakeControls.start({
      x: [0, -6, 7, -8, 5, -4, 3, -2, 1, 0],
      y: [0, 4, -3, 5, -4, 2, -2, 1, 0, 0],
      transition: { duration: 0.65, ease: "easeOut" },
    });
  });

  // Generate random twinkling stars once (matches Login page style)
  const twinkleStars = useMemo(() => 
    Array.from({ length: 45 }, (_, i) => ({
      id: i,
      top: Math.random() * 100,
      left: Math.random() * 100,
      size: 1.5 + Math.random() * 3,
      delay: Math.random() * 5,
      duration: 1.5 + Math.random() * 3,
      color: Math.random() > 0.85 
        ? (Math.random() > 0.5 ? 'rgba(16,185,129,0.9)' : 'rgba(34,211,238,0.9)') 
        : 'rgba(255,255,255,0.95)',
    })), []);

  // Calculate age from birthdate
  const calculateAge = (dob) => {
    const today = new Date();
    const birth = new Date(dob);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  // Check if user is 18+
  const isAdult = birthDate ? calculateAge(birthDate) >= 18 : false;

  // Fetch captcha config on mount - DISABLED for testing
  useEffect(() => {
    // reCAPTCHA disabled - will be enabled for production domain
    setCaptchaConfig({ enabled: false, type: 'disabled', site_key: '' });
  }, []);

  // Get reCAPTCHA Enterprise token
  const getCaptchaToken = async (action) => {
    if (!captchaConfig.enabled || captchaConfig.type !== 'enterprise') {
      return "";
    }
    
    try {
      if (window.grecaptcha && window.grecaptcha.enterprise) {
        const token = await window.grecaptcha.enterprise.execute(captchaConfig.site_key, { action });
        return token;
      }
    } catch (error) {
      console.error("reCAPTCHA Enterprise error:", error);
    }
    return "";
  };

  // Generate shooting stars periodically
  useEffect(() => {
    const createShootingStar = () => {
      const id = Date.now() + Math.random();
      const star = {
        id,
        top: Math.random() * 40 + '%',
        right: Math.random() * 30 + '%',
        delay: Math.random() * 0.5,
      };
      setShootingStars(prev => [...prev, star]);
      setTimeout(() => {
        setShootingStars(prev => prev.filter(s => s.id !== id));
      }, 2500);
    };

    // Create shooting stars every 3-6 seconds (less frequent for perf)
    const interval = setInterval(() => {
      if (Math.random() > 0.4) createShootingStar();
    }, 3000);

    // Initial stars
    setTimeout(createShootingStar, 1000);

    return () => clearInterval(interval);
  }, []);

  // Auto-play music on page load
  useEffect(() => {
    const randomTrack = LANDING_MUSIC[Math.floor(Math.random() * LANDING_MUSIC.length)];
    audioRef.current = new Audio(randomTrack);
    audioRef.current.loop = true;
    audioRef.current.volume = 0.3;
    
    // Try to auto-play
    const playMusic = async () => {
      try {
        await audioRef.current.play();
        setIsMusicPlaying(true);
      } catch (e) {
        // Auto-play blocked, will play on user interaction
      }
    };
    
    playMusic();
    
    // Play on any user interaction if auto-play was blocked
    const handleInteraction = () => {
      if (audioRef.current && !isMusicPlaying) {
        audioRef.current.play().then(() => {
          setIsMusicPlaying(true);
        }).catch(() => {});
      }
    };
    
    document.addEventListener('click', handleInteraction, { once: true });
    document.addEventListener('touchstart', handleInteraction, { once: true });
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
    };
  }, []);

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !audioRef.current.muted;
      setMusicMuted(!musicMuted);
    }
  };

  useEffect(() => {
    setArcsData(generateArcs());
  }, []);

  // Fetch business count for countdown
  useEffect(() => {
    const fetchBusinessCount = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/directory/businesses`);
        if (response.ok) {
          const data = await response.json();
          const list = Array.isArray(data.businesses) ? data.businesses : [];
          const count = data.total || list.length || 0;
          setBusinessCount(count);
          // Take the most-recent 8 (sorted by created_at desc)
          const sorted = [...list].sort((a, b) => {
            const da = new Date(a.created_at || 0).getTime();
            const dbt = new Date(b.created_at || 0).getTime();
            return dbt - da;
          });
          setRecentBusinesses(sorted.slice(0, 8));
        }
      } catch (error) {
        console.log("Could not fetch business count");
      }
    };
    
    fetchBusinessCount();
    // Refresh every 30 seconds
    const interval = setInterval(fetchBusinessCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Rotate the "recently joined" ticker every 3.5s
  useEffect(() => {
    if (recentBusinesses.length === 0) return;
    const i = setInterval(() => {
      setTickerIndex(prev => (prev + 1) % recentBusinesses.length);
    }, 3500);
    return () => clearInterval(i);
  }, [recentBusinesses.length]);

  // Animate countdown display
  useEffect(() => {
    const target = 100000 - businessCount;
    const duration = 2000; // 2 seconds animation
    const startTime = Date.now();
    const startValue = displayCount;
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Easing function for smooth animation
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(startValue + (target - startValue) * eased);
      setDisplayCount(current);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [businessCount]);

  // Fetch dynamic cities from API
  useEffect(() => {
    const fetchDynamicCities = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/globe/cities`);
        if (response.ok) {
          const data = await response.json();
          if (data.cities && data.cities.length > 0) {
            setDynamicCities(data.cities);
          }
        }
      } catch (error) {
        console.log("Using static cities for globe");
      }
    };
    
    fetchDynamicCities();
    // Refresh every 30 seconds to show new users joining cities
    const interval = setInterval(fetchDynamicCities, 30000);
    return () => clearInterval(interval);
  }, []);

  // Combine static and dynamic cities, prioritizing user cities
  const globeCities = useMemo(() => {
    if (dynamicCities.length === 0) return CITIES;
    
    // Create a map of dynamic cities by name (lowercase)
    const dynamicMap = new Map();
    dynamicCities.forEach(city => {
      dynamicMap.set(city.name.toLowerCase(), city);
    });
    
    // Merge: use dynamic data if available, otherwise use static
    const merged = [];
    const seen = new Set();
    
    // Add dynamic cities first (user-joined cities)
    dynamicCities.forEach(city => {
      merged.push({
        ...city,
        isDynamic: true,
        pop: city.user_count || 1
      });
      seen.add(city.name.toLowerCase());
    });
    
    // Add static cities that aren't in dynamic list
    CITIES.forEach(city => {
      if (!seen.has(city.name.toLowerCase())) {
        merged.push({
          ...city,
          isDynamic: false
        });
        seen.add(city.name.toLowerCase());
      }
    });
    
    return merged;
  }, [dynamicCities]);

  // Filter labels to prevent geographic overlap — keep only the most important city within 10° radius
  const labelCities = useMemo(() => {
    const candidates = globeCities
      .filter(c => c.isDynamic || c.pop > 5000000)
      .sort((a, b) => (b.pop || 0) - (a.pop || 0));
    const kept = [];
    for (const city of candidates) {
      const tooClose = kept.some(k => {
        const dLat = Math.abs(k.lat - city.lat);
        const dLng = Math.abs(k.lng - city.lng);
        return dLat < 10 && dLng < 15;
      });
      if (!tooClose) kept.push(city);
    }
    return kept;
  }, [globeCities]);

  // Auto-rotate globe - smooth and constant
  // CRITICAL FIX: Completely disable OrbitControls to allow page scrolling
  useEffect(() => {
    if (globeRef.current && globeReady) {
      const controls = globeRef.current.controls();
      
      // CRITICAL FIX FOR DESKTOP SCROLLING:
      // Completely disable OrbitControls - this is the definitive fix
      // This removes ALL event listeners that OrbitControls adds
      controls.enabled = false;
      
      // Configure auto-rotate (works even with controls.enabled = false)
      controls.autoRotate = true;
      controls.autoRotateSpeed = 0.8;
      
      // Set initial view - slightly angled for cinematic feel
      globeRef.current.pointOfView({ lat: 20, lng: -30, altitude: 2.5 });
      
      // Ensure canvas doesn't capture any events (belt and suspenders).
      // CRITICAL: must use `pan-y` (not `none`) so Android browsers still let
      // the user scroll vertically when their finger lands on the globe area.
      // `pointer-events: none` already blocks canvas-level interaction.
      const renderer = globeRef.current.renderer();
      if (renderer && renderer.domElement) {
        const canvas = renderer.domElement;
        canvas.style.pointerEvents = 'none';
        canvas.style.touchAction = 'pan-y';
      }
      
      // Animation loop for auto-rotate (controls.update() still works for autoRotate)
      let animationId;
      const animate = () => {
        if (globeRef.current && controls) {
          controls.update();
        }
        animationId = requestAnimationFrame(animate);
      };
      animate();
      
      // Cleanup on unmount
      return () => {
        if (animationId) {
          cancelAnimationFrame(animationId);
        }
      };
    }
  }, [globeReady]);

  const handleCityClick = useCallback((city) => {
    setSelectedCity(city);
    if (globeRef.current) {
      globeRef.current.pointOfView({ lat: city.lat, lng: city.lng, altitude: 1.5 }, 1500);
    }
  }, []);

  const filteredCities = globeCities.filter(
    (c) =>
      c.name.toLowerCase().includes(citySearch.toLowerCase()) ||
      (c.country && c.country.toLowerCase().includes(citySearch.toLowerCase()))
  );

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailLogin = () => {
    if (!email || !password) {
      toast.error("Please enter email and password");
      return;
    }
    setLoading(true);
    
    // Use XMLHttpRequest to avoid fetch interception by analytics scripts
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${BACKEND_URL}/api/auth/login`, true);
    xhr.setRequestHeader("Content-Type", "application/json");
    
    xhr.onload = function() {
      setLoading(false);
      if (xhr.status === 200) {
        try {
          const data = JSON.parse(xhr.responseText);
          if (data.session_token) {
            localStorage.setItem('sqawk_session', data.session_token);
            localStorage.setItem('sqawk_session_start', Date.now().toString());
            setUser(data.user);
            toast.success("Welcome back to sQawk!");
            navigate("/dashboard");
          } else {
            toast.error("Invalid server response");
          }
        } catch (err) {
          toast.error("Error processing login");
        }
      } else if (xhr.status === 401) {
        toast.error("Invalid email or password");
      } else {
        try {
          const data = JSON.parse(xhr.responseText);
          toast.error(data.detail || "Login failed");
        } catch {
          toast.error("Login failed");
        }
      }
    };
    
    xhr.onerror = function() {
      setLoading(false);
      toast.error("Network error - check your connection");
    };
    
    xhr.send(JSON.stringify({ email, password }));
  };

  const handleEmailRegister = () => {
    if (!name || !username || !email || !password) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    
    // Use XMLHttpRequest to avoid fetch interception issues
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${BACKEND_URL}/api/auth/register`, true);
    xhr.setRequestHeader("Content-Type", "application/json");
    
    xhr.onload = function() {
      setLoading(false);
      if (xhr.status === 200 || xhr.status === 201) {
        try {
          const data = JSON.parse(xhr.responseText);
          
          // Check if account is pending approval
          if (data.status === 'pending_approval') {
            setShowAuthModal(false);
            navigate('/pending-approval');
            return;
          }
          
          if (data.session_token) {
            localStorage.setItem('sqawk_session', data.session_token);
            localStorage.setItem('sqawk_session_start', Date.now().toString());
            setPendingUser(data.user);
            setShowAuthModal(false);
            setShowSuccessAnimation(true);
            setSuccessAnimationPhase(1);
            
            setTimeout(() => setSuccessAnimationPhase(2), 1500);
            setTimeout(() => setSuccessAnimationPhase(3), 3000);
            setTimeout(() => {
              setUser(data.user);
              navigate("/dashboard", { state: { user: data.user } });
            }, 4500);
          } else {
            toast.error("Invalid response from server");
          }
        } catch (err) {
          toast.error("Error processing registration");
        }
      } else {
        try {
          const data = JSON.parse(xhr.responseText);
          toast.error(data.detail || "Registration failed");
        } catch (err) {
          toast.error("Registration failed");
        }
      }
    };
    
    xhr.onerror = function() {
      setLoading(false);
      toast.error("Network error - check your connection");
    };
    
    xhr.send(JSON.stringify({ email, password, name, username, city, state, country, birth_date: birthDate, traveling_to: travelingTo }));
  };

  return (
    <>
      <PageSEO.Home />
      <div 
        className="bg-[#020617]" 
        id="landing-page-root" 
        style={{ 
          minHeight: '100vh',
          position: 'relative'
        }}
      >
      {/* Registration Success Animation */}
      {showSuccessAnimation && (
        <div className="fixed inset-0 z-[100] bg-slate-950 flex flex-col items-center justify-center">
          {/* Phase 1: Big Logo */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ 
              scale: successAnimationPhase >= 1 ? [0, 1.5, 1.2] : 0,
              rotate: successAnimationPhase >= 1 ? 0 : -180
            }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="mb-8"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_sqawk-social-4/artifacts/fvlilxsv_ChatGPT%20Image%20Jan%2027%2C%202026%2C%2007_59_06%20PM.png" 
              alt="sQawk" 
              className="w-48 h-48 object-contain drop-shadow-[0_0_50px_rgba(16,185,129,0.5)]"
            />
          </motion.div>
          
          {/* Phase 2: Loading Animation */}
          {successAnimationPhase >= 2 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center gap-4"
            >
              <div className="flex gap-2">
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    className="w-3 h-3 rounded-full bg-emerald-500"
                    animate={{
                      y: [0, -15, 0],
                      opacity: [0.5, 1, 0.5]
                    }}
                    transition={{
                      duration: 0.6,
                      repeat: Infinity,
                      delay: i * 0.15
                    }}
                  />
                ))}
              </div>
              <p className="text-white/60 text-sm">Setting up your sQawk...</p>
            </motion.div>
          )}
          
          {/* Phase 3: Success Message */}
          {successAnimationPhase >= 3 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: "spring", stiffness: 200 }}
              className="mt-8 text-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: [0, 1.2, 1] }}
                transition={{ duration: 0.5 }}
                className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center mx-auto mb-4"
              >
                <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </motion.div>
              <h2 className="text-2xl font-bold text-white mb-2">Your sQawk account was created!</h2>
              <p className="text-emerald-400">Welcome to the community, {pendingUser?.name?.split(' ')[0]}! 🎉</p>
            </motion.div>
          )}
        </div>
      )}
      
      {/* Immersive Space Starfield - Pure CSS, loads instantly */}
      <div className="starfield-container">
        <div className="galaxy-band" />
        <div className="cosmic-dust" />
        <div className="starfield-layer starfield-back" />
        <div className="starfield-layer starfield-mid" />
        <div className="starfield-layer starfield-front" />
        <div className="nebula nebula-1" />
        <div className="nebula nebula-2" />
        <div className="nebula nebula-3" />
      </div>

      {/* Individual twinkling stars — each flickers independently */}
      <div className="fixed inset-0 z-[1] pointer-events-none">
        {twinkleStars.map(star => (
          <div
            key={star.id}
            className="twinkle-star"
            style={{
              position: 'absolute',
              top: `${star.top}%`,
              left: `${star.left}%`,
              width: `${star.size}px`,
              height: `${star.size}px`,
              borderRadius: '50%',
              backgroundColor: star.color,
              boxShadow: `0 0 ${star.size * 2}px ${star.size * 0.5}px ${star.color}`,
              animationDelay: `${star.delay}s`,
              animationDuration: `${star.duration}s`,
            }}
          />
        ))}
      </div>

      {/* Shooting Stars */}
      <div className="fixed inset-0 z-[2] pointer-events-none overflow-hidden">
        {shootingStars.map(star => (
          <div
            key={star.id}
            className="shooting-star"
            style={{
              top: star.top,
              right: star.right,
              animationDelay: `${star.delay}s`,
            }}
          />
        ))}
      </div>

      {/* Scroll-rocket lives INSIDE the Mission Statement card now — see below. */}

      {/* 3D Globe Background - Lazy loaded with smooth entrance */}
      <motion.div 
        className="globe-container" 
        style={{ pointerEvents: 'none' }}
        initial={{ opacity: 0, scale: 0.7 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 2, ease: "easeOut" }}
      >
        <Suspense fallback={null}>
          <Globe
            ref={globeRef}
            globeImageUrl="//unpkg.com/three-globe/example/img/earth-blue-marble.jpg"
            bumpImageUrl="//unpkg.com/three-globe/example/img/earth-topology.png"
            backgroundImageUrl=""
            backgroundColor="rgba(0,0,0,0)"
            atmosphereColor="#10b981"
            atmosphereAltitude={0.35}
            pointsData={globeCities}
            pointLat="lat"
            pointLng="lng"
            pointColor={(d) => d.isDynamic ? "rgba(16, 185, 129, 0.9)" : "rgba(34, 211, 238, 0.7)"}
            pointAltitude={(d) => d.isDynamic ? 0.025 : 0.015}
            pointRadius={(d) => d.isDynamic ? 0.7 : 0.5}
            pointsMerge={true}
            ringsData={globeCities}
            ringLat="lat"
            ringLng="lng"
            ringColor={() => t => `rgba(16, 185, 129, ${1 - t})`}
            ringMaxRadius={3}
            ringPropagationSpeed={2}
            ringRepeatPeriod={1400}
            labelsData={labelCities}
            labelLat="lat"
            labelLng="lng"
            labelText={(d) => d.name}
            labelSize={(d) => d.isDynamic ? 1.8 : 1.5}
            labelDotRadius={(d) => d.isDynamic ? 0.6 : 0.5}
            labelColor={(d) => d.isDynamic ? "rgba(16, 185, 129, 1)" : "rgba(255, 255, 255, 0.9)"}
            labelResolution={2}
            labelAltitude={0.02}
            arcsData={arcsData}
            arcColor="color"
            arcDashLength={0.4}
            arcDashGap={0.3}
            arcDashAnimateTime={5000}
            arcStroke={0.5}
            arcAltitude={0.25}
            arcAltitudeAutoScale={0.4}
            onGlobeReady={() => {
              setGlobeReady(true);
              // Make the Three.js scene background transparent so CSS starfield shows through
              if (globeRef.current) {
                const scene = globeRef.current.scene();
                if (scene) scene.background = null;
                const renderer = globeRef.current.renderer();
                if (renderer) renderer.setClearColor(0x000000, 0);
              }
            }}
            onPointClick={handleCityClick}
            width={window.innerWidth}
            height={window.innerHeight}
            animateIn={false}
            rendererConfig={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
          />
        </Suspense>
      </motion.div>

      {/* Overlay gradient - subtle vignette for depth */}
      <div className="fixed top-0 left-0 w-full h-full bg-gradient-to-b from-transparent via-transparent to-slate-950/80 z-[3] pointer-events-none" />

      {/* Main Content - Scrollable container */}
      <div className="relative z-20" id="landing-scroll-container">
        <div className="min-h-screen flex flex-col">
        {/* Language Flags - Top Center with Preview Tooltips */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-center gap-1 sm:gap-2 pt-6 pb-2 px-2"
          style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 1.5rem)' }}
        >
          {languages.map((lang) => {
            const preview = translations[lang.code];
            return (
              <div key={lang.code} className="relative group">
                <button
                  onClick={() => handleLanguageChange(lang.code)}
                  className={`text-xl sm:text-2xl hover:scale-125 transition-all duration-200 ${language === lang.code ? 'scale-125 drop-shadow-lg ring-2 ring-emerald-500/50 rounded-full' : 'opacity-70 hover:opacity-100'}`}
                  aria-label={`Switch to ${lang.name}`}
                >
                  {lang.flag}
                </button>
                {/* Preview Tooltip - Hidden on mobile, shown on hover for desktop */}
                <div className="hidden md:block absolute top-full left-1/2 -translate-x-1/2 mt-2 opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none z-50">
                  <div className="bg-slate-900/95 backdrop-blur-lg border border-emerald-500/30 rounded-xl p-3 shadow-xl shadow-emerald-500/10 min-w-[200px] max-w-[250px]">
                    {/* Language name */}
                    <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-700">
                      <span className="text-lg">{lang.flag}</span>
                      <span className="text-white font-semibold text-sm">{lang.name}</span>
                    </div>
                    {/* Preview content */}
                    <p className="text-emerald-400 text-xs font-medium mb-1">{preview?.tagline || "sQawk Across the World."}</p>
                    <p className="text-slate-400 text-[10px] leading-relaxed line-clamp-2">{preview?.missionP3End || "Social Media for People."}</p>
                    {/* Click hint */}
                    <div className="mt-2 pt-2 border-t border-slate-700 flex items-center justify-center gap-1">
                      <span className="text-emerald-500 text-[10px]">Click to switch</span>
                      <svg className="w-3 h-3 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </div>
                  </div>
                  {/* Arrow pointer */}
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-slate-900/95 border-l border-t border-emerald-500/30 rotate-45" />
                </div>
              </div>
            );
          })}
        </motion.div>

        {/* Header */}
        <header className="flex items-center justify-between px-6 md:px-8 py-4">
          <motion.div
            initial={{ opacity: 0, x: -80, scale: 0.5 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            transition={{ duration: 1.4, type: "spring", stiffness: 40, damping: 14 }}
            className="flex items-center gap-3"
          >
            <img src={SQAWK_LOGO} alt="sQawk" className="h-28 md:h-36 lg:h-44 w-auto" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 80, scale: 0.5 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            transition={{ duration: 1.4, type: "spring", stiffness: 40, damping: 14, delay: 0.2 }}
            className="flex flex-col items-end gap-2"
          >
            <Button
              className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white btn-scale shadow-lg shadow-emerald-500/30"
              onClick={() => {
                const form = document.querySelector('[data-testid="create-profile-cta"]');
                if (form) {
                  form.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }}
              data-testid="get-started-btn"
            >
              sQawk Today
            </Button>
            <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30">
              <Shield className="w-3 h-3 text-emerald-400" />
              <span className="text-emerald-400 text-xs font-medium">{t.secure}</span>
            </div>
          </motion.div>
        </header>

        {/* Hero Section — wrapped in a motion.div for the one-shot ignition shake */}
        <motion.main animate={heroShakeControls} className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          {/* sQawk Across the World Section */}
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0.5, duration: 1.4, type: "spring", stiffness: 35, damping: 14 }}
            className="max-w-4xl"
          >
            <motion.h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-4 leading-tight text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-cyan-400 to-teal-400"
              initial={{ opacity: 0, y: 40, filter: "blur(10px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              transition={{ delay: 0.7, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            >
              {t.tagline}
            </motion.h1>
            
            {/* Quick Login Box */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="mb-6"
            >
              <button
                onClick={() => setShowLoginOverlay(true)}
                className="inline-flex items-center gap-2 px-6 py-2 rounded-full bg-slate-800/60 border border-emerald-500/30 text-white/90 hover:bg-emerald-500/20 hover:border-emerald-500/50 transition-all text-sm"
                data-testid="quick-login-btn"
              >
                <LogIn className="w-4 h-4 text-emerald-400" />
                {t.alreadyHaveAccount} <span className="text-emerald-400 font-semibold">{t.login}</span>
              </button>
            </motion.div>

            {/* Mission Statement (with large rocket above the text) */}
            <motion.div
              className="mb-8 mx-auto max-w-lg glass-card rounded-2xl px-5 pt-2 pb-5 border border-emerald-500/20 relative"
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.6, duration: 0.7, type: "spring" }}
              style={{ overflow: "visible" }}
            >
              {/* === Launch Pad: large centered rocket above text. Launches up on scroll. === */}
              <div className="relative w-full flex items-end justify-center mt-1 mb-2 pointer-events-none select-none" style={{ height: "clamp(180px, 36vw, 240px)" }} aria-hidden="true">
                {/* Smoke clip layer — keeps the volumetric cloud bounded inside the card horizontally
                    while still letting the rocket fly UP past the top. A soft radial mask fades the
                    cloud edges to transparent so it never shows hard rectangle edges. */}
                <div
                  className="absolute inset-x-0 bottom-0 h-[58%]"
                  style={{
                    overflow: "hidden",
                    borderBottomLeftRadius: "1rem",
                    borderBottomRightRadius: "1rem",
                    WebkitMaskImage: "radial-gradient(ellipse 70% 95% at 50% 90%, #000 55%, rgba(0,0,0,0.5) 78%, transparent 100%)",
                    maskImage: "radial-gradient(ellipse 70% 95% at 50% 90%, #000 55%, rgba(0,0,0,0.5) 78%, transparent 100%)",
                  }}
                >
                  {/* Volumetric smoke cluster — multiple overlapping puffs that billow outward
                      on scroll-down and gather back in on scroll-up. Centered tight under the rocket. */}
                  <motion.div
                    className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[72%] h-full"
                    style={{
                      scale: smokeScale,
                      opacity: smokeOpacity,
                      transformOrigin: "center bottom",
                    }}
                  >
                    {/* Ground-impact bloom (widest soft halo, centered) */}
                    <div
                      className="absolute bottom-[-2%] left-1/2 -translate-x-1/2 w-[100%] h-[55%] rounded-full"
                      style={{
                        background: "radial-gradient(ellipse at center, rgba(255,255,255,0.55) 0%, rgba(226,232,240,0.3) 38%, rgba(148,163,184,0.12) 68%, transparent 85%)",
                        filter: "blur(20px)",
                      }}
                    />
                    {/* Six soft puffs — symmetric around center, drift independently for organic feel */}
                    {[
                      { l: "32%", b: "0%",  w: "36%", h: "70%", a: 0.82, blur: 12, dur: 4.2, dx: 4, dy: -3 },
                      { l: "50%", b: "-2%", w: "40%", h: "82%", a: 0.88, blur: 14, dur: 5.0, dx: -5, dy: -4 },
                      { l: "40%", b: "12%", w: "32%", h: "60%", a: 0.72, blur: 10, dur: 4.6, dx: 4, dy: -5 },
                      { l: "20%", b: "8%",  w: "28%", h: "50%", a: 0.6,  blur: 9,  dur: 5.4, dx: -3, dy: -4 },
                      { l: "62%", b: "10%", w: "28%", h: "52%", a: 0.62, blur: 9,  dur: 4.8, dx: 4, dy: -3 },
                      { l: "44%", b: "30%", w: "22%", h: "40%", a: 0.55, blur: 8,  dur: 5.2, dx: -3, dy: -5 },
                    ].map((p, i) => (
                      <motion.div
                        key={i}
                        className="absolute rounded-full"
                        style={{
                          left: p.l, bottom: p.b, width: p.w, height: p.h,
                          background: `radial-gradient(ellipse at 45% 35%, rgba(255,255,255,${p.a}) 0%, rgba(226,232,240,${p.a * 0.6}) 35%, rgba(148,163,184,${p.a * 0.25}) 65%, transparent 82%)`,
                          filter: `blur(${p.blur}px)`,
                          transform: "translateX(-50%)",
                          marginLeft: 0,
                        }}
                        animate={{
                          x: [0, p.dx, 0, -p.dx * 0.5, 0],
                          y: [0, p.dy, p.dy * 0.5, p.dy * 0.8, 0],
                          scale: [1, 1.08, 1.04, 1.06, 1],
                        }}
                        transition={{ duration: p.dur, repeat: Infinity, ease: "easeInOut", delay: i * 0.25 }}
                      />
                    ))}
                  </motion.div>

                  {/* Rising exhaust trail — a vertical column of soft puffs that climb UPWARD
                      during launch, simulating smoke billowing out from beneath the rocket as
                      it lifts. Only visible while scroll-driven `trailOpacity` is non-zero. */}
                  <motion.div
                    className="absolute left-1/2 bottom-[8%] -translate-x-1/2 w-[34%] h-[120%]"
                    style={{
                      opacity: trailOpacity,
                      scaleY: trailScaleY,
                      transformOrigin: "center bottom",
                    }}
                  >
                    {[
                      { b: "0%",  w: "100%", h: "30%", a: 0.85, blur: 10, dur: 1.8, delay: 0,    rise: 18 },
                      { b: "18%", w: "92%",  h: "26%", a: 0.78, blur: 9,  dur: 2.0, delay: 0.18, rise: 22 },
                      { b: "34%", w: "82%",  h: "24%", a: 0.7,  blur: 9,  dur: 2.2, delay: 0.32, rise: 24 },
                      { b: "52%", w: "70%",  h: "22%", a: 0.6,  blur: 8,  dur: 2.4, delay: 0.5,  rise: 26 },
                      { b: "68%", w: "58%",  h: "20%", a: 0.5,  blur: 7,  dur: 2.5, delay: 0.7,  rise: 28 },
                      { b: "84%", w: "44%",  h: "16%", a: 0.38, blur: 6,  dur: 2.7, delay: 0.95, rise: 30 },
                    ].map((p, i) => (
                      <motion.div
                        key={`trail-${i}`}
                        className="absolute left-1/2 rounded-full"
                        style={{
                          bottom: p.b,
                          width: p.w,
                          height: p.h,
                          marginLeft: `-${parseInt(p.w) / 2}%`,
                          background: `radial-gradient(ellipse at 50% 45%, rgba(255,255,255,${p.a}) 0%, rgba(226,232,240,${p.a * 0.55}) 40%, rgba(148,163,184,${p.a * 0.2}) 70%, transparent 88%)`,
                          filter: `blur(${p.blur}px)`,
                        }}
                        animate={{
                          y: [0, -p.rise * 0.6, -p.rise],
                          opacity: [0.85, 1, 0.4],
                          scale: [0.85, 1.05, 1.2],
                        }}
                        transition={{ duration: p.dur, repeat: Infinity, ease: "easeOut", delay: p.delay }}
                      />
                    ))}
                  </motion.div>
                </div>

                {/* Heat shimmer / engine glow at the very base — pulses with the engines */}
                <motion.div
                  className="absolute bottom-[8%] left-1/2 -translate-x-1/2 w-[22%] h-[10%] rounded-full"
                  style={{
                    opacity: smokeOpacity,
                    background: "radial-gradient(ellipse at center, rgba(254,215,170,0.85) 0%, rgba(251,146,60,0.55) 40%, rgba(220,38,38,0.2) 70%, transparent 90%)",
                    filter: "blur(7px)",
                  }}
                  animate={{ scale: [1, 1.25, 0.95, 1.18, 1] }}
                  transition={{ duration: 0.9, repeat: Infinity, ease: "easeInOut" }}
                />

                {/* Spark trail — small bright sparks that climb up from the engines */}
                <motion.div
                  className="absolute bottom-[8%] left-1/2 -translate-x-1/2 w-[28%] h-[80%]"
                  style={{ opacity: smokeOpacity }}
                >
                  {[
                    { x: "48%", size: 4, dur: 1.4, delay: 0,    rise: 90, color: "#fde68a" },
                    { x: "58%", size: 3, dur: 1.6, delay: 0.25, rise: 80, color: "#fed7aa" },
                    { x: "38%", size: 3, dur: 1.5, delay: 0.5,  rise: 95, color: "#fcd34d" },
                    { x: "62%", size: 2, dur: 1.3, delay: 0.7,  rise: 70, color: "#fef3c7" },
                    { x: "44%", size: 2, dur: 1.7, delay: 1.0,  rise: 100, color: "#fbbf24" },
                  ].map((s, i) => (
                    <motion.span
                      key={i}
                      className="absolute rounded-full"
                      style={{
                        left: s.x,
                        bottom: 0,
                        width: s.size,
                        height: s.size,
                        background: s.color,
                        boxShadow: `0 0 ${s.size * 2.5}px ${s.color}, 0 0 ${s.size * 5}px rgba(251,146,60,0.7)`,
                      }}
                      animate={{
                        y: [0, -s.rise * 0.5, -s.rise],
                        opacity: [0, 1, 0],
                        scale: [0.6, 1, 0.4],
                      }}
                      transition={{ duration: s.dur, repeat: Infinity, ease: "easeOut", delay: s.delay }}
                    />
                  ))}
                </motion.div>
                {/* Rocket — uses scroll-driven transforms for the launch */}
                <motion.div
                  className="relative h-full"
                  style={{
                    y: rocketY,
                    rotate: rocketRotate,
                    scale: rocketScale,
                    opacity: rocketOpacity,
                    transformOrigin: "center bottom",
                    willChange: "transform, opacity",
                    backfaceVisibility: "hidden",
                  }}
                  data-testid="scroll-rocket"
                >
                  {/* Idle hover bob — wrapped so it composes with the scroll transforms */}
                  <motion.div
                    className="h-full"
                    animate={{ y: [-4, 4, -4] }}
                    transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut" }}
                    style={{ willChange: "transform" }}
                  >
                    <img
                      src="/rocket-launch.png"
                      alt=""
                      draggable="false"
                      className="h-full w-auto object-contain mx-auto drop-shadow-[0_18px_28px_rgba(220,38,38,0.35)] drop-shadow-[0_0_45px_rgba(251,146,60,0.25)]"
                      style={{
                        // Fade out the bottom of the PNG so its baked-in smoke
                        // doesn't lift away with the rocket. mask-image is
                        // GPU-accelerated on iOS Safari but expensive on
                        // Android Chrome — accept the trade-off because the
                        // visual benefit is significant on desktop / iOS.
                        WebkitMaskImage: "linear-gradient(to top, transparent 0%, rgba(0,0,0,0.35) 12%, #000 26%, #000 100%)",
                        maskImage: "linear-gradient(to top, transparent 0%, rgba(0,0,0,0.35) 12%, #000 26%, #000 100%)",
                        transform: "translateZ(0)",
                      }}
                    />
                  </motion.div>
                </motion.div>
              </div>

              <p className="text-white/90 text-xs sm:text-sm mb-3 leading-relaxed">
                {t.missionP1}
              </p>
              <p className="text-slate-300 text-xs sm:text-sm mb-3">
                <span className="text-emerald-400 font-semibold">{t.missionP2Intro}</span>{" "}
                {t.missionP2}
              </p>
              <p className="text-white/80 text-xs sm:text-sm">
                {t.missionP3Start}{" "}
                <span className="text-emerald-400 font-semibold">{t.missionP3End}</span>
              </p>
            </motion.div>
          </motion.div>

          {/* === INTERGALACTIC LIVE COUNTER + GLOBAL BUSINESS DIRECTORY === */}
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0.8, duration: 1.0, type: "spring", stiffness: 50, damping: 16 }}
            className="mb-12 max-w-3xl w-full mx-auto px-2"
            data-testid="live-countdown"
          >
            <div className="relative rounded-3xl overflow-hidden border border-emerald-400/25 bg-gradient-to-br from-slate-950/95 via-emerald-950/30 to-slate-950/95 backdrop-blur-2xl shadow-[0_0_60px_-15px_rgba(16,185,129,0.4)]">
              {/* Cosmic dust orbs */}
              <div aria-hidden="true" className="pointer-events-none absolute -top-24 -right-20 w-72 h-72 rounded-full bg-emerald-500/15 blur-3xl" />
              <div aria-hidden="true" className="pointer-events-none absolute -bottom-24 -left-16 w-80 h-80 rounded-full bg-cyan-500/15 blur-3xl" />
              <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, white 1px, transparent 1px), radial-gradient(circle at 80% 60%, white 1px, transparent 1px)", backgroundSize: "60px 60px, 90px 90px" }} />

              <div className="relative px-5 sm:px-8 py-6 sm:py-8">
                {/* LIVE pulse pill */}
                <div className="flex justify-center mb-4">
                  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-500/15 border border-rose-400/40">
                    <span className="relative inline-flex">
                      <span className="absolute inline-flex h-2 w-2 rounded-full bg-rose-400 animate-ping" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-rose-400" />
                    </span>
                    <span className="text-rose-200 text-[10px] font-bold uppercase tracking-[0.2em]">{t.liveCounter || "Live Counter"}</span>
                  </span>
                </div>

                {/* Headline */}
                <h2 className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 text-center text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.05] bg-gradient-to-br from-white via-emerald-100 to-cyan-200 bg-clip-text text-transparent">
                  <motion.span
                    aria-hidden="true"
                    initial={{ y: 6, rotate: -8 }}
                    animate={{ y: [-2, 4, -2], rotate: [-8, -3, -8] }}
                    transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut" }}
                    className="inline-flex shrink-0"
                  >
                    <Rocket className="w-9 h-9 sm:w-11 sm:h-11 text-emerald-300 drop-shadow-[0_0_12px_rgba(16,185,129,0.7)]" />
                  </motion.span>
                  <span>{t.globalBusinessDirectory || "100,000 Global Business Directory"}</span>
                </h2>
                <p className="mt-3 text-center text-slate-300/90 text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
                  {t.bePartOfGlobal || "Be Part of a GLOBAL Experiment! Our goal is to host"} <span className="text-emerald-300 font-semibold">100,000</span> {t.businessesWorldwide || "businesses worldwide."}
                </p>

                {/* Odometer-style counter */}
                <div className="mt-7 flex flex-col items-center">
                  <p className="text-emerald-300/80 text-[10px] font-bold uppercase tracking-[0.3em] mb-3">{t.spotsRemaining || "Spots Remaining"}</p>
                  <div className="flex items-center justify-center gap-1 sm:gap-1.5" data-testid="counter-digits">
                    {displayCount.toLocaleString().split("").map((char, i) => (
                      <motion.span
                        key={`${i}-${char}`}
                        initial={{ y: -12, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: i * 0.04, type: "spring", stiffness: 220, damping: 18 }}
                        className={
                          char === ","
                            ? "text-emerald-300/70 text-3xl sm:text-5xl font-black px-0.5"
                            : "relative inline-flex items-center justify-center min-w-[2.2rem] sm:min-w-[3rem] px-2 sm:px-3 py-2 sm:py-3 rounded-xl bg-gradient-to-b from-slate-900 to-slate-950 border border-emerald-400/30 text-white text-3xl sm:text-5xl font-black font-mono shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_8px_18px_-6px_rgba(16,185,129,0.5)]"
                        }
                      >
                        {char}
                      </motion.span>
                    ))}
                  </div>
                  <p className="mt-3 text-slate-400 text-xs sm:text-sm">{t.outOf100k || "out of 100,000"}</p>
                </div>

                {/* Orbital progress bar */}
                <div className="mt-7 max-w-md mx-auto">
                  <div className="h-2 rounded-full bg-slate-800/70 overflow-hidden border border-emerald-500/20 relative">
                    <motion.div
                      className="h-full rounded-full bg-gradient-to-r from-emerald-400 via-cyan-400 to-blue-400"
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.max(0.4, Math.min((businessCount / 100000) * 100, 100))}%` }}
                      transition={{ duration: 2, ease: "easeOut" }}
                      style={{ boxShadow: "0 0 18px rgba(16,185,129,0.8)" }}
                    />
                  </div>
                  <div className="flex justify-between mt-2 text-[11px]">
                    <span className="text-emerald-300 font-semibold">{businessCount.toLocaleString()} {t.joinedCount || "joined"}</span>
                    <span className="text-slate-500">{t.goalText || "Goal: 100K"}</span>
                  </div>
                </div>

                {/* Recently-joined rotating ticker (live social proof) */}
                {recentBusinesses.length > 0 && (
                  <div className="mt-5 max-w-md mx-auto h-9 flex items-center justify-center" data-testid="recent-businesses-ticker">
                    <AnimatePresence mode="wait">
                      {(() => {
                        const biz = recentBusinesses[tickerIndex % recentBusinesses.length];
                        if (!biz) return null;
                        const place = [biz.city, biz.country].filter(Boolean).join(", ");
                        // Position-based shoutout: 100,000 - businessCount + (tickerIndex offset to suggest individual spots)
                        const spotNumber = businessCount - tickerIndex;
                        return (
                          <motion.button
                            key={biz.id || biz.business_name}
                            initial={{ opacity: 0, y: 10, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -10, scale: 0.98 }}
                            transition={{ duration: 0.4, ease: "easeOut" }}
                            onClick={() => navigate("/directory")}
                            className="group inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 hover:bg-emerald-500/15 border border-emerald-400/25 hover:border-emerald-400/40 transition-colors max-w-full"
                            data-testid={`ticker-item-${tickerIndex}`}
                          >
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-400 text-[10px]" aria-hidden="true">🎉</span>
                            <span className="text-emerald-100/95 text-[12px] truncate">
                              <span className="font-semibold">{biz.business_name || biz.name || "A new business"}</span>
                              <span className="text-slate-400"> just claimed spot </span>
                              <span className="text-emerald-300 font-bold">#{spotNumber > 0 ? spotNumber : 1}</span>
                              {place && <span className="text-slate-500 hidden sm:inline"> · {place}</span>}
                            </span>
                          </motion.button>
                        );
                      })()}
                    </AnimatePresence>
                  </div>
                )}

                {/* Pay $1 CTA */}
                <div className="mt-8 flex flex-col items-center gap-2">
                  <Button
                    size="lg"
                    className="rounded-full bg-gradient-to-r from-emerald-500 via-cyan-500 to-blue-500 hover:from-emerald-600 hover:via-cyan-600 hover:to-blue-600 text-white font-bold px-7 sm:px-10 py-5 text-sm sm:text-base shadow-[0_0_30px_-6px_rgba(16,185,129,0.7)] hover:shadow-[0_0_45px_-4px_rgba(16,185,129,0.9)] transition-shadow"
                    onClick={() => navigate("/directory")}
                    data-testid="directory-cta-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    {t.clickPayAddBusiness || "Pay $1 & Add Your Business"}
                  </Button>
                  <p className="text-slate-500 text-[11px] tracking-wide">{t.noAccountRequired || "No account required · Instant listing · Global reach"}</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Create Your sQawk Profile - Registration Form */}
          <motion.div
            id="signup-form"
            initial={{ opacity: 0, y: 60, scale: 0.85 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.9, type: "spring", stiffness: 50 }}
            className="mb-12 max-w-xl w-full"
          >
            <div 
              className="glass-card rounded-2xl p-6 border border-emerald-500/30"
              data-testid="create-profile-cta"
            >
              <div className="flex flex-col items-center justify-center gap-4 text-center">
                {/* Creator Photo */}
                <div className="w-24 h-24 rounded-full overflow-hidden border-3 border-emerald-500/50 shadow-lg shadow-emerald-500/30">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_chirp-network-18/artifacts/adqmzr3i_391732030_10163185386338677_4125834605459463980_n.jpg" 
                    alt="Greg Miceli - Creator"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="text-center mb-4">
                  <h3 className="text-xl font-bold text-white">{t.createProfile}</h3>
                  <p className="text-slate-400 text-sm">{t.joinGlobal}</p>
                </div>
                
                {/* Inline Registration Form */}
                <div className="w-full space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t.name}
                        className="bg-slate-800/50 border-slate-700 text-white text-sm"
                        data-testid="landing-name-input"
                      />
                    </div>
                    <div>
                      <Input
                        value={username}
                        onChange={(e) => setUsername(e.target.value.replace(/^@+/, ''))}
                        placeholder={`@${t.username}`}
                        className="bg-slate-800/50 border-slate-700 text-white text-sm"
                        data-testid="landing-username-input"
                      />
                    </div>
                  </div>
                  
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t.email}
                    className="bg-slate-800/50 border-slate-700 text-white text-sm"
                    data-testid="landing-email-input"
                  />
                  
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t.password}
                    className="bg-slate-800/50 border-slate-700 text-white text-sm"
                    data-testid="landing-password-input"
                  />
                  
                  {/* Age Verification - 18+ Required */}
                  <div>
                    <Label className="text-slate-300 flex items-center gap-2 mb-1.5 text-xs">
                      <Shield className="w-3 h-3 text-amber-400" />
                      Date of Birth <span className="text-amber-400">(Must be 18+)</span>
                    </Label>
                    <Input
                      type="date"
                      value={birthDate}
                      onChange={(e) => setBirthDate(e.target.value)}
                      max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                      className="bg-slate-800/50 border-slate-700 text-white text-sm w-full [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-60"
                      data-testid="landing-birthdate-input"
                    />
                    {birthDate && !isAdult && (
                      <p className="text-red-400 text-xs mt-1.5">You must be 18 or older to join sQawk</p>
                    )}
                    {birthDate && isAdult && (
                      <div className="mt-1.5">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={ageConfirmed} 
                            onChange={(e) => setAgeConfirmed(e.target.checked)}
                            className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-emerald-500 focus:ring-emerald-500"
                            data-testid="landing-age-confirm"
                          />
                          <span className="text-slate-300 text-xs">I confirm I am 18 years or older</span>
                        </label>
                      </div>
                    )}
                  </div>
                  
                  {/* Location Fields */}
                  <div className="grid grid-cols-3 gap-2">
                    <Input
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder={t.city}
                      className="bg-slate-800/50 border-slate-700 text-white text-sm"
                      data-testid="landing-city-input"
                    />
                    <Input
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      placeholder={t.state}
                      className="bg-slate-800/50 border-slate-700 text-white text-sm"
                      data-testid="landing-state-input"
                    />
                    <Input
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      placeholder={t.country}
                      className="bg-slate-800/50 border-slate-700 text-white text-sm"
                      data-testid="landing-country-input"
                    />
                  </div>
                  
                  {/* Create Account Button */}
                  
                  {/* Security Badge */}
                  <div className="flex items-center justify-center gap-2 text-xs text-slate-400">
                    <Shield className="w-3 h-3 text-emerald-400" />
                    <span>{t.dataSecure}</span>
                  </div>
                  
                  <Button
                    type="button"
                    onClick={handleEmailRegister}
                    className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 disabled:opacity-50"
                    disabled={loading || !isAdult || !ageConfirmed}
                    data-testid="landing-register-btn"
                  >
                    {loading ? t.creatingAccount : t.createAccount}
                  </Button>
                  
                  {(!isAdult || !ageConfirmed) && birthDate && (
                    <p className="text-center text-amber-400 text-xs">Please confirm you are 18+ to create an account</p>
                  )}
                  
                  <p className="text-center text-slate-500 text-xs">
                    Already have an account? <button onClick={() => setShowLoginOverlay(true)} className="text-emerald-400 hover:underline">Login</button>
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* ====== STEP INSIDE: GMB 360 + Check-Ins Marketing Box ====== */}
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.95 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, type: "spring", stiffness: 60, damping: 18 }}
            className="mt-10 max-w-4xl w-full px-4"
            data-testid="step-inside-marketing-box"
          >
            <div className="relative rounded-3xl overflow-hidden border border-fuchsia-400/20 bg-gradient-to-br from-slate-950/90 via-violet-950/40 to-slate-950/90 backdrop-blur-xl p-6 sm:p-10 shadow-[0_0_60px_-15px_rgba(217,70,239,0.35)]">
              {/* Cosmic dust orbs */}
              <div aria-hidden="true" className="pointer-events-none absolute -top-24 -right-20 w-72 h-72 rounded-full bg-fuchsia-500/15 blur-3xl" />
              <div aria-hidden="true" className="pointer-events-none absolute -bottom-24 -left-16 w-80 h-80 rounded-full bg-cyan-500/10 blur-3xl" />
              <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, white 1px, transparent 1px), radial-gradient(circle at 80% 60%, white 1px, transparent 1px)", backgroundSize: "60px 60px, 90px 90px" }} />

              <div className="relative">
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-fuchsia-500/15 border border-fuchsia-400/30 text-fuchsia-200 text-[10px] font-bold uppercase tracking-[0.18em]">
                    <Sparkles className="w-3 h-3" /> New on the Globe
                  </span>
                </div>
                <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight bg-gradient-to-br from-white via-fuchsia-100 to-cyan-100 bg-clip-text text-transparent leading-[1.05]">
                  Step Inside, From <br className="hidden sm:block" /> Anywhere in the Universe.
                </h3>
                <p className="mt-4 text-slate-300/90 text-base sm:text-lg leading-relaxed max-w-2xl">
                  Every pin on the sQawk Globe doubles as a teleport. We harness <span className="text-fuchsia-300 font-semibold">Google My Business 360°</span> imagery to drop you inside cafés, studios and storefronts before you ever set foot in them — exterior to interior, in a single tap. <span className="text-cyan-300 font-semibold">Check-Ins write straight back to the Google My Business graph</span>, so when you tag a place on sQawk, the world's most-trusted local index hears the echo too.
                </p>

                <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { icon: Camera, title: "360° Inside & Out", body: "Walk the sidewalk, then walk through the door. Indoor panoramas surface a glowing Step Inside badge.", iconWrap: "bg-fuchsia-500/15 border-fuchsia-400/30", iconColor: "text-fuchsia-300" },
                    { icon: Compass, title: "Cinematic Precision Zoom", body: "Search a venue and watch the Globe descend in three smooth stages — regional, neighborhood, city-block.", iconWrap: "bg-cyan-500/15 border-cyan-400/30", iconColor: "text-cyan-300" },
                    { icon: Building2, title: "Check-Ins That Travel", body: "Tag a place on sQawk and your visit echoes through Google My Business — real-world signal from social-world energy.", iconWrap: "bg-emerald-500/15 border-emerald-400/30", iconColor: "text-emerald-300" },
                  ].map((p, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.15 + i * 0.12, duration: 0.6 }}
                      whileHover={{ y: -4 }}
                      className="rounded-2xl bg-white/[0.03] border border-white/10 p-5 backdrop-blur-sm hover:border-white/20 transition-colors"
                    >
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 border ${p.iconWrap}`}>
                        <p.icon className={`w-5 h-5 ${p.iconColor}`} />
                      </div>
                      <h4 className="text-white font-semibold text-sm mb-1.5">{p.title}</h4>
                      <p className="text-slate-400 text-[12px] leading-relaxed">{p.body}</p>
                    </motion.div>
                  ))}
                </div>

                <p className="mt-6 text-[11px] text-slate-500/80 italic">
                  Powered by Google Maps Platform · Street View Service · Places API. sQawk does not modify Google data — we surface it like constellations.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Feature Cards - 6 Features - Intergalactic Fly-In */}
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 max-w-7xl w-full px-4">
            {[
              { icon: Plane, title: t.findTravel, desc: t.connectTravelers, color: "from-sky-500 to-blue-500", fromX: -120, fromY: -80 },
              { icon: GlobeIcon, title: t.collectCities, desc: t.buildFootprint, color: "from-emerald-500 to-teal-500", fromX: 80, fromY: -100 },
              { icon: Music, title: t.shareAnthem, desc: t.showcaseMusic, color: "from-cyan-500 to-blue-500", fromX: -100, fromY: 60 },
              { icon: Users, title: t.top5Squad, desc: t.showcaseClosestFriends, color: "from-pink-500 to-rose-500", fromX: 120, fromY: 80 },
              { icon: Megaphone, title: t.promoteYourself, desc: t.getDiscovered, color: "from-amber-500 to-orange-500", fromX: -60, fromY: -120 },
              { icon: Briefcase, title: t.shareBusiness, desc: t.showcaseBrand, color: "from-violet-500 to-purple-500", fromX: 100, fromY: -60 },
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: feature.fromX, y: feature.fromY, scale: 0.3, rotate: (i % 2 === 0 ? -15 : 15) }}
                animate={{ opacity: 1, x: 0, y: 0, scale: 1, rotate: 0 }}
                transition={{ delay: 0.8 + i * 0.18, duration: 1.2, type: "spring", stiffness: 45, damping: 14 }}
                whileHover={{ scale: 1.08, y: -8, boxShadow: "0 20px 60px rgba(16,185,129,0.15)" }}
                className="glass-card rounded-2xl p-5 text-center hover:border-emerald-500/30 transition-colors duration-300 cursor-pointer group flex flex-col items-center"
              >
                <motion.div 
                  className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4 mx-auto`}
                  whileHover={{ rotate: [0, -10, 10, 0], scale: 1.15 }}
                  transition={{ duration: 0.4 }}
                >
                  <feature.icon className="w-6 h-6 text-white" />
                </motion.div>
                <h3 className="text-base font-semibold text-white mb-1 text-center">{feature.title}</h3>
                <p className="text-slate-400 text-sm text-center">{feature.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* App Services / Features from Welcome Tour */}
          <div className="mt-20 max-w-6xl w-full px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.8, duration: 0.6 }}
              className="text-center mb-10"
            >
              <h2 className="text-lg sm:text-xl font-bold text-white mb-2">Everything You Need, All in One Place</h2>
              <p className="text-slate-400 text-sm max-w-lg mx-auto">Built for creators, businesses, and communities worldwide</p>
            </motion.div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                {
                  icon: CalendarClock,
                  title: "My Calendar & Services",
                  desc: "Set up your availability, accept bookings, and share your QR code. Clients can book you directly.",
                  color: "from-teal-500 to-cyan-500",
                  link: "/my-schedule"
                },
                {
                  icon: Radio,
                  title: "Go Live",
                  desc: "Share moments in real-time with 5-minute live streams. No pressure — just authentic connection.",
                  color: "from-red-500 to-pink-500",
                  link: "/dashboard"
                },
                {
                  icon: Building2,
                  title: "100,000 Business Directory",
                  desc: "List your business globally for just $1. Freelancer, local shop, or global brand — everyone is welcome.",
                  color: "from-amber-500 to-orange-500",
                  link: "/directory"
                },
                {
                  icon: CalendarDays,
                  title: "Events & Workshops",
                  desc: "Discover events near you. Create your own with ticketing, scheduling, and a built-in community.",
                  color: "from-amber-500 to-yellow-500",
                  link: "/dashboard"
                },
                {
                  icon: Award,
                  title: "Verified sQawk Skills",
                  desc: "Get verified as a professional — realtor, artist, coach & more. Earn client trust with a verified badge.",
                  color: "from-amber-500 to-orange-500",
                  link: "/sqawker-skills"
                },
                {
                  icon: Heart,
                  title: "sQawk Non-Profits",
                  desc: "Support organizations making a difference. Browse non-profits, donate directly, and attend charity events.",
                  color: "from-rose-500 to-pink-500",
                  link: "/sqawk-nonprofits"
                },
                {
                  icon: QrCode,
                  title: "Digital Business Card",
                  desc: "Share your profile via QR code or link. Perfect for networking and making real-world connections.",
                  color: "from-violet-500 to-purple-500",
                  link: "/dashboard"
                },
                {
                  icon: GlobeIcon,
                  title: "Globe Explorer",
                  desc: "Explore sQawk activity worldwide on an interactive 3D globe. See live check-ins, trending cities, and events.",
                  color: "from-emerald-500 to-cyan-500",
                  link: "/dashboard"
                },
              ].map((service, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 2.0 + i * 0.1, duration: 0.5 }}
                  whileHover={{ y: -6, borderColor: "rgba(16,185,129,0.4)" }}
                  onClick={() => navigate(service.link)}
                  className="relative rounded-2xl p-5 cursor-pointer transition-all duration-300 group overflow-hidden"
                  style={{
                    background: "linear-gradient(160deg, rgba(15,23,42,0.9), rgba(15,23,42,0.6))",
                    border: "1px solid rgba(100,116,139,0.2)",
                    backdropFilter: "blur(12px)",
                  }}
                  data-testid={`service-card-${i}`}
                >
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{ background: `linear-gradient(135deg, ${service.color.includes('teal') ? 'rgba(20,184,166,0.08)' : service.color.includes('red') ? 'rgba(239,68,68,0.08)' : service.color.includes('amber') ? 'rgba(245,158,11,0.08)' : service.color.includes('rose') ? 'rgba(244,63,94,0.08)' : service.color.includes('violet') ? 'rgba(139,92,246,0.08)' : 'rgba(16,185,129,0.08)'}, transparent)` }}
                  />
                  <div className="relative z-10 flex flex-col items-center text-center">
                    <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-3 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <service.icon className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-white font-semibold text-sm mb-1.5">{service.title}</h3>
                    <p className="text-slate-400 text-xs leading-relaxed">{service.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
          
          {/* Claim Your City CTA */}
          <motion.div
            initial={{ opacity: 0, x: -100, rotate: -5 }}
            animate={{ opacity: 1, x: 0, rotate: 0 }}
            transition={{ delay: 0.9, duration: 0.8, type: "spring", stiffness: 60 }}
            whileHover={{ scale: 1.02 }}
            className="mt-16 mb-8 max-w-2xl w-full mx-auto"
          >
            <div className="glass-card rounded-2xl p-6 border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-orange-500/10 to-red-500/10">
              <div className="flex flex-col items-center text-center gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
                  <MapPin className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2 flex items-center justify-center gap-2">
                    <span>🏙️</span> {t.claimYourCity || "Claim Your City"}
                  </h3>
                  <p className="text-slate-300 text-sm mb-1">
                    {t.beFoundingMember || "Be the founding member of your hometown's sQawk community!"}
                  </p>
                  <p className="text-slate-400 text-sm">
                    {t.startConversations || "Start conversations, share local news, and connect with people in your city."}
                  </p>
                </div>
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold px-8 py-6 text-lg shadow-lg shadow-amber-500/30"
                  onClick={() => navigate('/communities?tab=cities')}
                  data-testid="claim-city-cta-btn"
                >
                  <MapPin className="w-5 h-5 mr-2" />
                  {t.searchClaimCity || "Search & Claim Your City!"}
                </Button>
                <p className="text-slate-500 text-xs">{t.freeToClaim || "Free to claim • Become a founding member • Build your local community"}</p>
              </div>
            </div>
          </motion.div>


          {/* Footer with Social Links - Ultra Compact */}
          <motion.footer
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
            className="mt-2 mb-4 max-w-md w-full mx-auto px-4"
          >
            <div className="rounded-lg p-3 border border-slate-700/20 bg-slate-900/30 backdrop-blur-sm">
              <div className="flex flex-col items-center gap-2">
                {/* Centered Logo and tagline */}
                <div className="flex items-center gap-3">
                  <img src={SQAWK_LOGO} alt="sQawk" className="h-12 w-auto" />
                  <p className="text-white text-sm font-semibold">Social Media for People</p>
                </div>

                {/* Pre-Launch Subscribe Box */}
                <div className="w-full mt-2 p-3 rounded-lg bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-emerald-500/10 border border-cyan-500/25" data-testid="prelaunch-subscribe-box">
                  {!subscribed ? (
                    <>
                      <p className="text-slate-300 text-[10px] text-center leading-tight mb-2">Subscribe to be an <span className="text-cyan-400 font-semibold">App Tester</span> for the sQawk Pre-Launch or be notified once features have officially launched!</p>
                      <form onSubmit={handleSubscribe} className="flex gap-1.5">
                        <input type="email" value={subscribeEmail} onChange={e => setSubscribeEmail(e.target.value)} placeholder="Enter your email" required
                          className="flex-1 px-2.5 py-1.5 rounded-md bg-slate-800/80 border border-slate-600/50 text-white text-xs placeholder:text-slate-500 focus:outline-none focus:border-cyan-500/50" data-testid="prelaunch-email-input" />
                        <button type="submit" disabled={subscribing}
                          className="px-3 py-1.5 rounded-md bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-xs font-medium hover:from-cyan-600 hover:to-blue-600 transition-all disabled:opacity-50 shrink-0" data-testid="prelaunch-subscribe-btn">
                          {subscribing ? '...' : 'Subscribe'}
                        </button>
                      </form>
                    </>
                  ) : (
                    <p className="text-emerald-400 text-xs text-center font-medium" data-testid="prelaunch-success">You're in! We'll notify you when sQawk launches.</p>
                  )}
                </div>

                {/* Social Links - Compact inline */}
                <div className="flex items-center gap-2">
                  <a
                    href="https://facebook.com/sqawkapp"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-7 h-7 rounded-full bg-blue-600/20 hover:bg-blue-600/40 border border-blue-500/30 flex items-center justify-center transition-all hover:scale-110"
                    data-testid="facebook-link"
                  >
                    <svg className="w-3.5 h-3.5 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>
                  <a
                    href="https://instagram.com/sqawkapp"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-7 h-7 rounded-full bg-pink-600/20 hover:bg-pink-600/40 border border-pink-500/30 flex items-center justify-center transition-all hover:scale-110"
                    data-testid="instagram-link"
                  >
                    <svg className="w-3.5 h-3.5 text-pink-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </a>
                  <a
                    href="/book/mrpositivity"
                    className="w-7 h-7 rounded-full bg-emerald-600/20 hover:bg-emerald-600/40 border border-emerald-500/30 flex items-center justify-center transition-all hover:scale-110"
                    data-testid="book-appointment-link"
                  >
                    <CalendarDays className="w-3.5 h-3.5 text-emerald-400" />
                  </a>
                  <span className="text-slate-600 text-[9px]">© 2026 sQawk™</span>
                </div>
              </div>
            </div>
          </motion.footer>
        </motion.main>

        {/* Selected City Info */}
        <AnimatePresence>
          {selectedCity && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="fixed bottom-8 left-1/2 -translate-x-1/2 glass-panel rounded-2xl p-6 flex items-center gap-6 border border-emerald-500/30"
            >
              <div>
                <h3 className="text-xl font-bold text-white">{selectedCity.name}</h3>
                <p className="text-slate-400">{selectedCity.country}</p>
              </div>
              <Button
                className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600"
                onClick={() => {
                  navigate('/register');
                  setSelectedCity(null);
                }}
              >
                Join this city
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedCity(null)}
                className="text-slate-400"
              >
                ✕
              </Button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* PWA Install Banner */}
        <AnimatePresence>
          {showInstallBanner && installPrompt && (
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 100 }}
              className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50"
              data-testid="install-banner"
            >
              <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-4 border border-emerald-500/30 shadow-2xl shadow-emerald-500/20">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                    <Download className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-white font-bold text-sm">Install sQawk App</h4>
                    <p className="text-slate-400 text-xs mt-1">Add to home screen for the best experience. Always get the latest updates!</p>
                  </div>
                  <button
                    onClick={dismissInstallBanner}
                    className="text-slate-500 hover:text-white transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button
                    size="sm"
                    className="flex-1 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white font-semibold"
                    onClick={handleInstallClick}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Install Now
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-slate-600 text-slate-300"
                    onClick={dismissInstallBanner}
                  >
                    Later
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        </div>{/* End of min-h-screen flex flex-col */}
      </div>{/* End of scroll container */}

      {/* Auth Modal */}
      <Dialog open={showAuthModal} onOpenChange={setShowAuthModal}>
        <DialogContent className="bg-slate-900 border-slate-700 sm:max-w-md" data-testid="auth-modal">
          {/* sQawk Logo at top of modal - BIGGER */}
          <div className="flex justify-center -mt-2 mb-4">
            <img 
              src={SQAWK_LOGO} 
              alt="sQawk" 
              className="w-56 h-56 sm:w-64 sm:h-64 object-contain"
            />
          </div>
          
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white text-center">
              {t.welcome}
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-center">
              {t.joinCommunity}
            </DialogDescription>
          </DialogHeader>

          <Tabs value={authTab} onValueChange={setAuthTab} className="mt-4">
            <TabsList className="grid w-full grid-cols-2 bg-slate-800">
              <TabsTrigger value="login" data-testid="login-tab">{t.login}</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">{t.signUp}</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="space-y-4 mt-4">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="login-email" className="text-slate-300">{t.email}</Label>
                  <Input
                    id="login-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="login-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="login-password" className="text-slate-300">{t.password}</Label>
                  <Input
                    id="login-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="login-password-input"
                  />
                </div>
                
                {/* Security Badge */}
                <div className="flex items-center justify-center gap-2 text-xs text-slate-400 pt-2">
                  <Shield className="w-3.5 h-3.5 text-emerald-400" />
                  <span>{t.dataSecure}</span>
                </div>
                
                <Button
                  type="button"
                  onClick={handleEmailLogin}
                  className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? t.signingIn : t.signIn}
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="register" className="space-y-4 mt-4">
              {/* sQawk Logo above registration form */}
              <div className="flex justify-center mb-4">
                <img 
                  src={SQAWK_LOGO} 
                  alt="sQawk" 
                  className="w-16 h-16 object-contain"
                />
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="register-name" className="text-slate-300">{t.name}</Label>
                    <Input
                      id="register-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      className="bg-slate-800 border-slate-700 text-white"
                      data-testid="register-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="register-username" className="text-slate-300">{t.username}</Label>
                    <Input
                      id="register-username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value.replace(/^@+/, ''))}
                      placeholder="@username"
                      className="bg-slate-800 border-slate-700 text-white"
                      data-testid="register-username-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="register-email" className="text-slate-300">{t.email}</Label>
                  <Input
                    id="register-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="register-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="register-password" className="text-slate-300">{t.password}</Label>
                  <Input
                    id="register-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="register-password-input"
                  />
                </div>
                
                {/* Age Verification */}
                <div>
                  <Label htmlFor="register-birthdate" className="text-slate-300 flex items-center gap-2 mb-1.5">
                    <Shield className="w-4 h-4 text-amber-400" />
                    Date of Birth <span className="text-amber-400 text-xs">(Must be 18+)</span>
                  </Label>
                  <Input
                    id="register-birthdate"
                    type="date"
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                    max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                    className="bg-slate-800 border-slate-700 text-white w-full [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-60"
                    data-testid="register-birthdate-input"
                  />
                  {birthDate && !isAdult && (
                    <p className="text-red-400 text-xs mt-1.5">You must be 18 or older to join sQawk</p>
                  )}
                  {birthDate && isAdult && (
                    <div className="mt-1.5">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={ageConfirmed} 
                          onChange={(e) => setAgeConfirmed(e.target.checked)}
                          className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-emerald-500 focus:ring-emerald-500"
                          data-testid="age-confirm-checkbox"
                        />
                        <span className="text-slate-300 text-sm">I confirm I am 18 years or older</span>
                      </label>
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label htmlFor="register-city" className="text-slate-300">{t.city} <span className="text-slate-500 text-xs">({t.optional})</span></Label>
                    <Input
                      id="register-city"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder={t.city}
                      className="bg-slate-800 border-slate-700 text-white"
                      data-testid="register-city-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="register-state" className="text-slate-300">{t.state} <span className="text-slate-500 text-xs">({t.optional})</span></Label>
                    <Input
                      id="register-state"
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      placeholder={t.state}
                      className="bg-slate-800 border-slate-700 text-white"
                      data-testid="register-state-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="register-country" className="text-slate-300">{t.country} <span className="text-slate-500 text-xs">({t.optional})</span></Label>
                    <Input
                      id="register-country"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      placeholder={t.country}
                      className="bg-slate-800 border-slate-700 text-white"
                      data-testid="register-country-input"
                    />
                  </div>
                </div>
                
                
                {/* Security Badge */}
                <div className="flex items-center justify-center gap-2 text-xs text-slate-400 pt-2">
                  <Shield className="w-3.5 h-3.5 text-emerald-400" />
                  <span>{t.dataSecure}</span>
                </div>
                
                <Button
                  type="button"
                  onClick={handleEmailRegister}
                  className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading || !isAdult || !ageConfirmed}
                  data-testid="register-submit-btn"
                >
                  {loading ? t.creatingAccount : t.createAccount}
                </Button>
                {(!isAdult || !ageConfirmed) && birthDate && (
                  <p className="text-center text-amber-400 text-xs">Please confirm you are 18+ to create an account</p>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* City Search Modal */}
      <Dialog open={showCitySearch} onOpenChange={setShowCitySearch}>
        <DialogContent className="bg-slate-900 border-slate-700 sm:max-w-lg" data-testid="city-search-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white">
              <MapPin className="inline w-5 h-5 mr-2 text-emerald-400" />
              Explore Cities
            </DialogTitle>
          </DialogHeader>

          <Input
            value={citySearch}
            onChange={(e) => setCitySearch(e.target.value)}
            placeholder="Search cities..."
            className="bg-slate-800 border-slate-700 text-white"
            data-testid="city-search-input"
          />

          <div className="mt-4 max-h-64 overflow-y-auto space-y-2">
            {filteredCities.map((city) => (
              <div
                key={city.name}
                className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 hover:bg-slate-800 cursor-pointer transition-colors"
                onClick={() => {
                  handleCityClick(city);
                  setShowCitySearch(false);
                }}
                data-testid={`city-${city.name.toLowerCase().replace(/\s/g, "-")}`}
              >
                <div>
                  <p className="text-white font-medium">{city.name}</p>
                  <p className="text-slate-400 text-sm">{city.country}</p>
                </div>
                <span className="text-slate-500 text-sm mono">
                  {city.lat.toFixed(2)}, {city.lng.toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Login Overlay */}
      <AnimatePresence>
        {showLoginOverlay && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[200] flex items-center justify-center p-4"
            data-testid="login-overlay"
          >
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              onClick={() => { if (!loginLoading) { setShowLoginOverlay(false); setLoginError(''); } }}
            />

            {/* Login Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 20 }}
              animate={{ opacity: loginSuccess ? 0 : 1, scale: loginSuccess ? 1.05 : 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 20 }}
              transition={{ type: "spring", stiffness: 350, damping: 30 }}
              className="relative w-full max-w-[360px] z-10"
            >
              {/* Close button */}
              <button
                onClick={() => { if (!loginLoading) { setShowLoginOverlay(false); setLoginError(''); } }}
                className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-slate-800 border border-slate-600 flex items-center justify-center text-slate-400 hover:text-white hover:border-emerald-500/50 transition-all z-20"
                data-testid="login-overlay-close"
              >
                <X className="w-4 h-4" />
              </button>

              <div className="bg-slate-900/90 backdrop-blur-xl rounded-2xl border border-emerald-500/25 shadow-[0_20px_60px_rgba(16,185,129,0.2),0_8px_24px_rgba(0,0,0,0.4)] overflow-hidden">
                {/* Header */}
                <div className="text-center pt-7 pb-4 px-6">
                  <motion.img 
                    src="https://customer-assets.emergentagent.com/job_01b76b10-720d-4884-b0e7-21e20cd7a47d/artifacts/0s7u1ici_ChatGPT%20Image%20Jan%2027%2C%202026%2C%2007_59_06%20PM.png" 
                    alt="sQawk" 
                    className="w-52 h-auto mx-auto mb-5 object-contain drop-shadow-[0_0_30px_rgba(37,99,235,0.35)]"
                    initial={{ opacity: 0, scale: 0.6, y: -20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ type: "spring", stiffness: 200, damping: 18, delay: 0.15 }}
                  />
                  <motion.h2
                    className="text-xl font-bold text-white"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.3 }}
                  >{t.welcomeBack || "Welcome Back"}</motion.h2>
                  <motion.p
                    className="text-slate-400 text-xs mt-1"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.4, delay: 0.45 }}
                  >{t.signIn || "Sign in"} to continue</motion.p>
                </div>

                {/* Form */}
                <motion.form
                  onSubmit={handleLoginSubmit}
                  className="px-6 pb-6 space-y-3"
                  data-testid="login-overlay-form"
                  initial="hidden"
                  animate="visible"
                  variants={{
                    hidden: {},
                    visible: { transition: { staggerChildren: 0.08, delayChildren: 0.4 } }
                  }}
                >
                  <motion.div variants={{ hidden: { opacity: 0, x: -16 }, visible: { opacity: 1, x: 0, transition: { type: "spring", stiffness: 300, damping: 24 } } }}>
                  <input
                    type="email"
                    placeholder={t.email || "Email"}
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-800/60 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/20 transition-all text-sm"
                    autoComplete="email"
                    autoFocus
                    data-testid="login-overlay-email"
                  />
                  </motion.div>
                  <motion.div variants={{ hidden: { opacity: 0, x: -16 }, visible: { opacity: 1, x: 0, transition: { type: "spring", stiffness: 300, damping: 24 } } }}>
                  <input
                    type="password"
                    placeholder={t.password || "Password"}
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-800/60 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/20 transition-all text-sm"
                    autoComplete="current-password"
                    data-testid="login-overlay-password"
                  />
                  </motion.div>

                  {loginError && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="p-2.5 bg-red-500/20 border border-red-500/30 rounded-lg text-red-300 text-xs text-center" data-testid="login-overlay-error">
                      {loginError}
                    </motion.div>
                  )}

                  <motion.div variants={{ hidden: { opacity: 0, x: -16 }, visible: { opacity: 1, x: 0, transition: { type: "spring", stiffness: 300, damping: 24 } } }}>
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={rememberMe}
                      onClick={() => setRememberMe(!rememberMe)}
                      className={`relative w-9 h-5 rounded-full transition-colors duration-200 ${rememberMe ? 'bg-emerald-500' : 'bg-slate-600'}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform duration-200 ${rememberMe ? 'translate-x-4' : ''}`} />
                    </button>
                    <span className="text-slate-400 text-xs">Remember me</span>
                  </label>
                  </motion.div>

                  <motion.div variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 250, damping: 20 } } }}>
                  <button
                    type="submit"
                    disabled={loginLoading}
                    className="w-full py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl text-white font-semibold transition-all duration-300 shadow-lg shadow-emerald-500/30 text-sm"
                    data-testid="login-overlay-submit"
                  >
                    {loginLoading ? (t.signingIn || 'Signing in...') : (t.signIn || 'Sign In')}
                  </button>
                  </motion.div>
                </motion.form>

                {/* Footer links */}
                <motion.div
                  className="px-6 pb-5 text-center space-y-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.7, duration: 0.4 }}
                >
                  <button 
                    onClick={() => { setShowLoginOverlay(false); navigate('/forgot-password'); }}
                    className="text-slate-400 hover:text-emerald-400 text-xs transition-colors"
                    data-testid="login-overlay-forgot"
                  >
                    Forgot password?
                  </button>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
    </>
  );
}
